import * as THREE from "three";
import {
  ClippingPolygonType,
  DoorType,
  DoubleDoorType,
  FillPattern,
  RoomType,
  SingleLineType,
  SlidingDoorType,
  SymbolType,
  WallFaceSide,
  WallShapeType,
  WallType,
  WallConnectionEnd,
  WallConnectionStart,
  WindowType,
  isDoubleDoorType,
  isAreaFillSymbolType,
  isDoorType,
  isSingleLeafDoorType,
  isSlidingDoorType,
  isVoidType,
  isWallType,
  isWindowType,
} from "../../../types/floorplannerTypes";
import { createShapes } from "../../../components/FloorPlan/createShapes";
import { applyClippingPolygonsToShapes } from "../../../components/FloorPlan/wallClippingGeometry";
import {
  getQuadraticBezierPoint,
  getQuadraticBezierTangent,
} from "../../../utils/arcUtils";
import { getSymbolInsertGeometry } from "../../../utils/symbolAttachmentGeometry";
import { isPointInPolygon } from "../../../utils/areaFillGeometry";
import { resolveWallCapFillColor } from "../../../utils/wallFaceSides";

export const DEFAULT_WALL_HEIGHT = 2.4;
export const DEFAULT_DOOR_HEIGHT = 2.1;
export const DEFAULT_WINDOW_SILL_HEIGHT = 0.9;
export const DEFAULT_WINDOW_HEIGHT = 1.2;
export const DEFAULT_FLOOR_THICKNESS = 0.08;
export const CURVED_OPENING_SEGMENTS = 48;

const MIN_GEOMETRY_DEPTH = 0.001;
const MIN_POLYGON_AREA = 1e-8;
const MIN_DOOR_LEAF_WIDTH = 0.05;
const MIN_DOOR_FRAME_WIDTH = 0.01;
const MIN_DOOR_THICKNESS = 0.02;
const MAX_DOOR_OPEN_ANGLE = Math.PI / 2;
const MIN_OPENING_SPAN = 0.01;
const DOOR_BOTTOM_GAP = 0.01;
const ARC_WALL_FOOTPRINT_SEGMENTS = 24;

export type Floor3DShapeDefinition = {
  id: string;
  shape: THREE.Shape;
  selectionShape?: THREE.Shape;
  fillColor?: number;
  fillOpacity?: number;
  fillPattern?: FillPattern;
  source: "areaFill" | "room" | "fallback";
  roomId?: string;
  roomSignature?: string;
  roomObjectIds?: string[];
};

export type Ceiling3DShapeDefinition = {
  id: string;
  floorId: string;
  shape: THREE.Shape;
  height: number;
  source: Floor3DShapeDefinition["source"];
  roomObjectIds?: string[];
};

export type WallEndpointHeights = {
  start: number;
  end: number;
};

export type HorizontalSurfaceOcclusionDefinition = {
  id: string;
  shape: THREE.Shape;
  z: number;
  hideWhenCameraIs: "above" | "below";
};

export type Door3DLeafGeometry = {
  id: string;
  hinge: THREE.Vector2;
  closedSign: 1 | -1;
  rotationSign: 1 | -1;
  rotation: number;
  width: number;
  thickness: number;
  height: number;
  bottomGap: number;
  panelCenter: THREE.Vector3;
  handleCenters: THREE.Vector3[];
  angleHandleCenter: THREE.Vector3;
  hingeBarrels: Array<{
    center: THREE.Vector3;
    radius: number;
    height: number;
  }>;
};

export type Door3DBoxGeometry = {
  id: string;
  center: THREE.Vector3;
  size: THREE.Vector3;
  rotationY?: number;
};

export type Door3DFrameGeometry = Door3DBoxGeometry;

export type Door3DAssemblyGeometry = {
  origin: THREE.Vector2;
  rotation: number;
  openingWidth: number;
  frameWidth: number;
  wallWidth: number;
  doorHeight: number;
  leafHeight: number;
  leaves: Door3DLeafGeometry[];
  frames: Door3DFrameGeometry[];
  casings: Door3DBoxGeometry[];
  stops: Door3DBoxGeometry[];
  miterGrooves: Door3DBoxGeometry[];
};

export type Window3DBoxGeometry = Door3DBoxGeometry;

export type Window3DLeafGeometry = {
  id: string;
  hinge: THREE.Vector2;
  closedSign: 1 | -1;
  rotationSign: 1 | -1;
  rotation: number;
  width: number;
  depth: number;
  height: number;
  baseZ: number;
  frameWidth: number;
  sashFrames: Window3DBoxGeometry[];
  glassPane: Window3DBoxGeometry;
  handleCenters: THREE.Vector3[];
  angleHandleCenter: THREE.Vector3;
  hingeBarrels: Array<{
    center: THREE.Vector3;
    radius: number;
    height: number;
  }>;
};

export type Window3DAssemblyGeometry = {
  origin: THREE.Vector2;
  rotation: number;
  openingWidth: number;
  wallWidth: number;
  frameWidth: number;
  sillHeight: number;
  windowHeight: number;
  sashDepth: number;
  leaf: Window3DLeafGeometry;
  frames: Window3DBoxGeometry[];
  casings: Window3DBoxGeometry[];
  miterGrooves: Window3DBoxGeometry[];
};

type Door3DSymbol = DoorType | SlidingDoorType | DoubleDoorType;

type Door3DAssemblyOptions = {
  defaultFrameWidth: number;
  defaultBladeThickness: number;
  wallWidth: number;
  doorHeight?: number;
};

type Window3DAssemblyOptions = {
  defaultFrameWidth: number;
  wallWidth: number;
  wallHeight: number;
};

export type StraightWallOpeningGeometryOptions = {
  wallShape?: WallShapeType;
  wallWidth: number;
  wallHeight: number;
  defaultDoorFrameWidth: number;
  defaultWindowFrameWidth: number;
  clippingPolygons?: ClippingPolygonType[];
  /**
   * Optional local-X span (metres along wall tangent from start) to build only
   * one mid-junction face segment of a continuous wall.
   */
  xRange?: { min: number; max: number };
  /**
   * Which broad faces (along wall length) to include. Left = local +Y / left
   * normal; right = local -Y. Use with includeStructure to split materials.
   */
  broadFaces?: "left" | "right" | "both" | "none";
  /**
   * Structure faces (top/bottom/endcaps/openings). Prefer the object form when
   * coloring caps to match per-side face paints.
   */
  includeStructure?:
    | boolean
    | {
        horizontal?: boolean;
        /** true/"both" = both ends of this xRange; or only the wall start/end. */
        endCaps?: boolean | "start" | "end" | "both";
        openings?: boolean;
      };
  /**
   * Limit horizontal/end-cap structure to one half of the wall thickness so the
   * top strip can match the left or right face color.
   */
  structureSide?: "left" | "right" | "both";
};

type StraightWallOpening = {
  xMin: number;
  xMax: number;
  zMin: number;
  zMax: number;
  topBulge?: number;
};

type LocalWallFrame = {
  tangent: THREE.Vector2;
  normal: THREE.Vector2;
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
};

type WallInteriorSideCandidate = {
  interiorNormal: THREE.Vector2;
  interiorReference: THREE.Vector3;
  source: "room" | "fallback";
};

export function getWall3DHeight(
  wall: WallType,
  fallback = DEFAULT_WALL_HEIGHT,
): number {
  const height = wall.threeD?.height;
  return Number.isFinite(height) && typeof height === "number" && height > 0
    ? height
    : fallback;
}

function getExplicitWall3DHeight(wall: WallType): number | null {
  const height = wall.threeD?.height;
  return typeof height === "number" && Number.isFinite(height) && height > 0
    ? height
    : null;
}

/**
 * Resolve the height at each wall endpoint without adding persisted endpoint
 * properties. An explicitly-heighted wall drives an otherwise-default joint;
 * when several explicit walls disagree, the higher value wins so the joint is
 * always watertight and deterministic.
 */
export function createWallEndpointHeightMap(
  walls: WallType[],
  fallback = DEFAULT_WALL_HEIGHT,
): Map<string, WallEndpointHeights> {
  const wallsById = new Map(walls.map((wall) => [wall.id, wall]));

  const resolveEndpoint = (
    wall: WallType,
    endpoint: "start" | "end",
  ): number => {
    const endpointPosition =
      endpoint === "start" ? WallConnectionStart : WallConnectionEnd;
    const connectedWallIds = new Set<string>();

    wall.centerConnections
      ?.filter((connection) => connection.sourcePosition === endpointPosition)
      .forEach((connection) => connectedWallIds.add(connection.id));
    wall.edgeConnections
      ?.filter((connection) => connection.sourceEndpoint === endpoint)
      .forEach((connection) =>
        connectedWallIds.add(connection.targetWallId),
      );

    const jointWalls = [
      wall,
      ...Array.from(connectedWallIds)
        .map((wallId) => wallsById.get(wallId))
        .filter((candidate): candidate is WallType => Boolean(candidate)),
    ];
    const explicitHeights = jointWalls
      .map(getExplicitWall3DHeight)
      .filter((height): height is number => height !== null);

    if (explicitHeights.length > 0) {
      return Math.max(...explicitHeights);
    }
    return Math.max(
      ...jointWalls.map((candidate) => getWall3DHeight(candidate, fallback)),
    );
  };

  return new Map(
    walls.map((wall) => [
      wall.id,
      {
        start: resolveEndpoint(wall, "start"),
        end: resolveEndpoint(wall, "end"),
      },
    ]),
  );
}

export function getWallHeightAtPoint(
  wall: WallType,
  endpointHeights: WallEndpointHeights,
  point: THREE.Vector2,
): number {
  const progress = getWallProgressAtPoint(wall, point);
  return THREE.MathUtils.lerp(
    endpointHeights.start,
    endpointHeights.end,
    progress,
  );
}

export function getWallHeightAtDistance(
  wall: WallType,
  endpointHeights: WallEndpointHeights,
  positionFromStart: number,
): number {
  const length =
    wall.lineForm === "arc" && wall.controlPoint
      ? sampleWallReferencePoints(wall, 32).reduce(
          (total, point, index, points) =>
            index === 0 ? 0 : total + point.distanceTo(points[index - 1]),
          0,
        )
      : wall.start.distanceTo(wall.end);
  const progress = length > 1e-8 ? clamp(positionFromStart / length, 0, 1) : 0;
  return THREE.MathUtils.lerp(
    endpointHeights.start,
    endpointHeights.end,
    progress,
  );
}

function getWallProgressAtPoint(wall: WallType, point: THREE.Vector2): number {
  const samples = sampleWallReferencePoints(
    wall,
    wall.lineForm === "arc" && wall.controlPoint ? 32 : 1,
  );
  let bestDistanceSq = Number.POSITIVE_INFINITY;
  let bestProgress = 0;

  for (let index = 0; index < samples.length - 1; index++) {
    const start = samples[index];
    const segment = samples[index + 1].clone().sub(start);
    const lengthSq = segment.lengthSq();
    const localProgress =
      lengthSq > 1e-12
        ? clamp(point.clone().sub(start).dot(segment) / lengthSq, 0, 1)
        : 0;
    const closest = start.clone().addScaledVector(segment, localProgress);
    const distanceSq = closest.distanceToSquared(point);
    if (distanceSq < bestDistanceSq) {
      bestDistanceSq = distanceSq;
      bestProgress = (index + localProgress) / (samples.length - 1);
    }
  }

  return bestProgress;
}

export function applyWallEndpointHeights(
  geometry: THREE.BufferGeometry,
  wall: WallType,
  endpointHeights: WallEndpointHeights,
  sourceHeight: number,
): void {
  const position = geometry.getAttribute("position");
  if (!position || sourceHeight <= 0) return;

  for (let index = 0; index < position.count; index++) {
    const topHeight = getWallHeightAtPoint(
      wall,
      endpointHeights,
      new THREE.Vector2(position.getX(index), position.getY(index)),
    );
    const currentZ = position.getZ(index);
    if (currentZ >= sourceHeight - 1e-5) {
      position.setZ(index, topHeight);
    } else if (currentZ > topHeight) {
      position.setZ(index, topHeight);
    }
  }

  position.needsUpdate = true;
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
}

export function getSymbol3DBaseElevation(
  symbol: SymbolType,
  fallback = 0,
): number {
  const baseElevation = symbol.threeD?.baseElevation;
  return Number.isFinite(baseElevation) && typeof baseElevation === "number"
    ? baseElevation
    : fallback;
}

export function createExtrudeGeometryFromShape(
  shape: THREE.Shape,
  depth: number,
): THREE.ExtrudeGeometry {
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: Math.max(depth, MIN_GEOMETRY_DEPTH),
    bevelEnabled: false,
  });
  geometry.computeBoundingBox();
  geometry.computeVertexNormals();
  return geometry;
}

/**
 * Limit a single-shape extrusion to its top/bottom lids ("caps") or its
 * vertical walls ("sides"). ExtrudeGeometry stores lids as material group 0
 * and walls as group 1, each contiguous for one shape.
 */
export function restrictExtrudeGeometryToPart(
  geometry: THREE.BufferGeometry,
  part: "caps" | "sides",
): void {
  const materialIndex = part === "caps" ? 0 : 1;
  const matching = geometry.groups.filter(
    (group) => group.materialIndex === materialIndex,
  );
  if (matching.length !== 1) return;
  geometry.setDrawRange(matching[0].start, matching[0].count);
}

export function applyWallTextureUVs(
  geometry: THREE.BufferGeometry,
  wall: WallType,
  positionZ = 0,
): void {
  const position = geometry.getAttribute("position");
  if (!position) return;

  if (!geometry.getAttribute("normal")) {
    geometry.computeVertexNormals();
  }

  const normalAttribute = geometry.getAttribute("normal");
  const wallVector = new THREE.Vector2().subVectors(wall.end, wall.start);
  const wallLength = wallVector.length();
  if (wallLength <= 1e-8) return;

  const tangent = wallVector.divideScalar(wallLength);
  const wallNormal = new THREE.Vector2(-tangent.y, tangent.x);
  const uvs: number[] = [];
  const patternUvs: number[] = [];
  const wallHeight = Math.max(getWall3DHeight(wall), MIN_GEOMETRY_DEPTH);

  geometry.computeBoundingBox();
  const boundingBox = geometry.boundingBox;
  let acrossMin = Number.POSITIVE_INFINITY;
  let acrossMax = Number.NEGATIVE_INFINITY;
  if (boundingBox) {
    for (let index = 0; index < position.count; index++) {
      const local = new THREE.Vector2(
        position.getX(index) - wall.start.x,
        position.getY(index) - wall.start.y,
      );
      const acrossWall = local.dot(wallNormal);
      acrossMin = Math.min(acrossMin, acrossWall);
      acrossMax = Math.max(acrossMax, acrossWall);
    }
  }
  const acrossSpan = Math.max(acrossMax - acrossMin, MIN_GEOMETRY_DEPTH);

  for (let index = 0; index < position.count; index++) {
    const local = new THREE.Vector2(
      position.getX(index) - wall.start.x,
      position.getY(index) - wall.start.y,
    );
    const alongWall = local.dot(tangent);
    const acrossWall = local.dot(wallNormal);
    const z = position.getZ(index) + positionZ;
    const uAlong = alongWall / wallLength;
    const uAcross = (acrossWall - acrossMin) / acrossSpan;
    const vHeight = z / wallHeight;

    const nx = normalAttribute?.getX(index) ?? 0;
    const ny = normalAttribute?.getY(index) ?? 0;
    const nz = normalAttribute?.getZ(index) ?? 0;
    const tangentInfluence = Math.abs(nx * tangent.x + ny * tangent.y);

    if (Math.abs(nz) > 0.75) {
      uvs.push(uAlong, uAcross);
      patternUvs.push(alongWall, acrossWall);
    } else if (tangentInfluence > 0.75) {
      uvs.push(uAcross, vHeight);
      patternUvs.push(acrossWall, z);
    } else {
      uvs.push(uAlong, vHeight);
      patternUvs.push(alongWall, z);
    }
  }

  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute(
    "uv1",
    new THREE.Float32BufferAttribute(patternUvs, 2),
  );
}

export function isStraightWallOpeningGeometrySupported(
  wall: WallType,
): boolean {
  return (
    wall.lineForm !== "arc" && wall.start.distanceToSquared(wall.end) > 1e-8
  );
}

function getDistinctValues(values: number[], tolerance = 1e-5): number[] {
  const distinct: number[] = [];
  values.forEach((value) => {
    if (!Number.isFinite(value)) return;
    if (!distinct.some((existing) => Math.abs(existing - value) <= tolerance)) {
      distinct.push(value);
    }
  });
  return distinct;
}

/**
 * Tolerance for treating projected footprint coords as the same edge.
 * Angled walls accumulate float noise; 1mm is still tight relative to wall width.
 */
const WALL_FRAME_RECT_TOLERANCE = 1e-3;

export function isWallShapeRectangularInWallFrame(
  wall: WallType,
  wallShape: WallShapeType | undefined,
): boolean {
  if (!wallShape || wallShape.paths.length < 4) return false;

  const wallVector = new THREE.Vector2().subVectors(wall.end, wall.start);
  const wallLength = wallVector.length();
  if (wallLength <= 1e-8) return false;

  const tangent = wallVector.divideScalar(wallLength);
  const normal = new THREE.Vector2(-tangent.y, tangent.x);
  const projected = wallShape.paths
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
    .map((point) => {
      const local = point.clone().sub(wall.start);
      return {
        x: local.dot(tangent),
        y: local.dot(normal),
      };
    });

  if (projected.length !== wallShape.paths.length || projected.length !== 4) {
    return false;
  }

  const xs = getDistinctValues(
    projected.map((point) => point.x),
    WALL_FRAME_RECT_TOLERANCE,
  );
  const ys = getDistinctValues(
    projected.map((point) => point.y),
    WALL_FRAME_RECT_TOLERANCE,
  );
  if (xs.length !== 2 || ys.length !== 2) return false;

  return projected.every(
    (point) =>
      xs.some((x) => Math.abs(x - point.x) <= WALL_FRAME_RECT_TOLERANCE) &&
      ys.some((y) => Math.abs(y - point.y) <= WALL_FRAME_RECT_TOLERANCE),
  );
}

export function hasArchedVoidOpening(
  symbols: Iterable<SymbolType | undefined> | undefined,
): boolean {
  if (!symbols) return false;
  for (const symbol of symbols) {
    if (!symbol || !isVoidType(symbol)) continue;
    const topBulge = symbol.threeD?.topBulge;
    if (
      typeof topBulge === "number" &&
      Number.isFinite(topBulge) &&
      Math.abs(topBulge) > 1e-6
    ) {
      return true;
    }
  }
  return false;
}

/**
 * True when the wall has any door/window/void that needs a 3D opening cutout.
 * Used to keep integrated opening meshes in sync with attachment slides
 * (arrow-key nudges) instead of relying on fragile 2D boolean fallback clips.
 */
export function hasWallOpeningSymbols(
  symbols: Iterable<SymbolType | undefined> | undefined,
): boolean {
  if (!symbols) return false;
  for (const symbol of symbols) {
    if (
      symbol &&
      (isSingleLeafDoorType(symbol) ||
        isDoubleDoorType(symbol) ||
        isWindowType(symbol) ||
        isVoidType(symbol))
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Integrated opening geometry is used for straight walls with a rectangular
 * footprint. Neighbor-wall clips normally force the extrusion fallback so
 * junctions stay on the old boolean clip. Walls with openings (or toppbåge)
 * opt in so holes follow attachment.positionFromStart; neighbor clips become
 * full-height slots instead of blocking integrated meshes.
 */
export function canUseIntegratedStraightWallGeometry(
  wall: WallType,
  wallShape: WallShapeType | undefined,
  clippingPolygons?: ClippingPolygonType[],
  options?: { allowNeighborWallClips?: boolean },
): boolean {
  if (
    !isStraightWallOpeningGeometrySupported(wall) ||
    !wallShape ||
    !isWallShapeRectangularInWallFrame(wall, wallShape)
  ) {
    return false;
  }
  if (options?.allowNeighborWallClips) return true;
  return !clippingPolygons?.some((clipping) => clipping.objectType === "wall");
}

/**
 * Shared gate for Wall3DMeshes + WallAttachment3DMeshes: use attachment-driven
 * opening cutouts whenever the wall has doors/windows/voids (including when
 * neighbor junction clips are present).
 */
export function shouldUseIntegratedOpeningGeometry(
  wall: WallType,
  wallShape: WallShapeType | undefined,
  clippingPolygons: ClippingPolygonType[] | undefined,
  openingSymbols: Iterable<SymbolType | undefined> | undefined,
  forceIntegrated = false,
): boolean {
  if (forceIntegrated) return true;
  return canUseIntegratedStraightWallGeometry(wall, wallShape, clippingPolygons, {
    allowNeighborWallClips: hasWallOpeningSymbols(openingSymbols),
  });
}

/**
 * Whether this wall should render selectable left/right face meshes.
 * Always allows neighbor clips so T-junction walls stay face-selectable;
 * Wall3DMeshes and WallAttachment3DMeshes must agree on this or lintels/sills
 * steal clicks without wallFaceSide and clear the face pick.
 */
export function canUseSelectableWallFaceGeometry(
  wall: WallType,
  wallShape: WallShapeType | undefined,
  clippingPolygons?: ClippingPolygonType[],
): boolean {
  return canUseIntegratedStraightWallGeometry(wall, wallShape, clippingPolygons, {
    allowNeighborWallClips: true,
  });
}

/**
 * Read left/right face side from mesh userData (integrated face meshes only).
 */
export function getWallFaceSideFromUserData(
  userData: { wallFaceSide?: unknown } | undefined | null,
): WallFaceSide | null {
  if (userData?.wallFaceSide === "left" || userData?.wallFaceSide === "right") {
    return userData.wallFaceSide;
  }
  return null;
}

/**
 * Closest left/right face hit for a wall from a pointer event's intersections.
 * Prefers a camera-facing face near the closest wall hit so a ray through an
 * opening/junction hole does not latch onto the opposite side.
 * If the nearest mesh of this wall is structure/top (no wallFaceSide), returns null.
 */
export function getClosestWallFaceSideFromIntersections(
  wallId: string,
  intersections: ReadonlyArray<{
    distance?: number;
    face?: { normal?: THREE.Vector3 } | null;
    object?: {
      userData?: { floorplannerId?: unknown; wallFaceSide?: unknown };
      matrixWorld?: THREE.Matrix4;
    };
  }>,
  rayDirection?: THREE.Vector3 | null,
): WallFaceSide | null {
  let nearestWallHitFound = false;
  let nearestFaceDistance = Number.POSITIVE_INFINITY;
  let bestFacing: WallFaceSide | null = null;
  let bestFacingDistance = Number.POSITIVE_INFINITY;
  let bestAny: WallFaceSide | null = null;
  let bestAnyDistance = Number.POSITIVE_INFINITY;

  for (const hit of intersections) {
    if (hit.object?.userData?.floorplannerId !== wallId) continue;
    const distance = typeof hit.distance === "number" ? hit.distance : 0;
    const side = getWallFaceSideFromUserData(hit.object.userData);

    if (!nearestWallHitFound) {
      nearestWallHitFound = true;
      // Nearest mesh on this wall is structure/top — whole-wall select.
      if (!side) return null;
    }
    if (!side) continue;

    if (distance < bestAnyDistance) {
      bestAnyDistance = distance;
      bestAny = side;
      nearestFaceDistance = distance;
    }

    if (rayDirection && hit.face?.normal && hit.object.matrixWorld) {
      const worldNormal = hit.face.normal
        .clone()
        .transformDirection(hit.object.matrixWorld);
      // Facing the camera / ray origin when normal points against the ray.
      if (worldNormal.dot(rayDirection) < -1e-4 && distance < bestFacingDistance) {
        bestFacingDistance = distance;
        bestFacing = side;
      }
    }
  }

  if (
    bestFacing &&
    bestFacingDistance <= nearestFaceDistance + 0.05
  ) {
    return bestFacing;
  }
  return bestAny;
}

/**
 * Resolve which wall face a click selected. Prefer the face under pointer-down
 * so a pointer-up that tunnels to the opposite face does not flip the pick.
 */
export function resolveWallFaceSideFromPointerEvents(
  wallId: string,
  pointerDownSide: WallFaceSide | null | undefined,
  intersections: ReadonlyArray<{
    distance?: number;
    face?: { normal?: THREE.Vector3 } | null;
    object?: {
      userData?: { floorplannerId?: unknown; wallFaceSide?: unknown };
      matrixWorld?: THREE.Matrix4;
    };
  }>,
  rayDirection?: THREE.Vector3 | null,
): WallFaceSide | null {
  if (pointerDownSide === "left" || pointerDownSide === "right") {
    return pointerDownSide;
  }
  return getClosestWallFaceSideFromIntersections(
    wallId,
    intersections,
    rayDirection,
  );
}

function createWallFootprintClippingPolygon(
  wall: WallType,
  defaultWallWidth: number,
): THREE.Vector2[] | null {
  const width = wall.wallWidth ?? defaultWallWidth;
  if (width <= MIN_OPENING_SPAN) return null;

  if (wall.lineForm === "arc" && wall.controlPoint) {
    const left: THREE.Vector2[] = [];
    const right: THREE.Vector2[] = [];
    const halfWidth = width / 2;

    for (let i = 0; i <= ARC_WALL_FOOTPRINT_SEGMENTS; i++) {
      const t = i / ARC_WALL_FOOTPRINT_SEGMENTS;
      const point = getQuadraticBezierPoint(
        t,
        wall.start,
        wall.controlPoint,
        wall.end,
      );
      const tangent = getQuadraticBezierTangent(
        t,
        wall.start,
        wall.controlPoint,
        wall.end,
      );
      const normal =
        tangent.lengthSq() > 1e-12
          ? new THREE.Vector2(-tangent.y, tangent.x).normalize()
          : new THREE.Vector2(0, 1);
      left.push(point.clone().addScaledVector(normal, halfWidth));
      right.push(point.clone().addScaledVector(normal, -halfWidth));
    }

    const polygon = [...left, ...right.reverse()];
    return Math.abs(signedPolygonArea(polygon)) > MIN_POLYGON_AREA
      ? polygon
      : null;
  }

  const wallVector = new THREE.Vector2().subVectors(wall.end, wall.start);
  const length = wallVector.length();
  if (length <= MIN_OPENING_SPAN) return null;

  const tangent = wallVector.divideScalar(length);
  const normal = new THREE.Vector2(-tangent.y, tangent.x).multiplyScalar(
    width / 2,
  );
  const polygon = [
    wall.start.clone().add(normal),
    wall.end.clone().add(normal),
    wall.end.clone().sub(normal),
    wall.start.clone().sub(normal),
  ];

  return Math.abs(signedPolygonArea(polygon)) > MIN_POLYGON_AREA
    ? polygon
    : null;
}

export function getConnectedWallFootprintClippings(
  wall: WallType,
  walls: Iterable<WallType>,
  defaultWallWidth = 0.2,
): ClippingPolygonType[] {
  const orderedWalls = Array.from(walls);
  const wallIndex = orderedWalls.findIndex(
    (candidate) => candidate.id === wall.id,
  );
  if (wallIndex < 0) return [];

  const clippings: ClippingPolygonType[] = [];
  const seen = new Set<string>();

  for (
    let otherIndex = wallIndex + 1;
    otherIndex < orderedWalls.length;
    otherIndex++
  ) {
    const ownerWall = orderedWalls[otherIndex];
    if (!areWallsEdgeConnected(wall, ownerWall)) {
      continue;
    }

    if (seen.has(ownerWall.id)) {
      continue;
    }

    const polygon = createWallFootprintClippingPolygon(
      ownerWall,
      defaultWallWidth,
    );
    if (!polygon) {
      continue;
    }

    seen.add(ownerWall.id);
    clippings.push({
      belongsTo: ownerWall.id,
      objectType: "wall",
      polygon,
    });
  }

  return clippings;
}

export function getWallOpeningGeometryClippings(
  wall: WallType,
  walls: Iterable<WallType>,
  defaultWallWidth = 0.2,
  wallClippings: ClippingPolygonType[] | undefined,
): ClippingPolygonType[] {
  const connectedWallClippings = getConnectedWallFootprintClippings(
    wall,
    walls,
    defaultWallWidth,
  );
  return wallClippings?.length
    ? [...wallClippings, ...connectedWallClippings]
    : connectedWallClippings;
}

function areWallsEdgeConnected(wallA: WallType, wallB: WallType): boolean {
  const hasConnection = (source: WallType, target: WallType): boolean =>
    source.edgeConnections?.some(
      (connection) =>
        connection.sourceWallId === source.id &&
        connection.targetWallId === target.id,
    ) ?? false;

  return hasConnection(wallA, wallB) || hasConnection(wallB, wallA);
}

export function signedPolygonArea(points: THREE.Vector2[]): number {
  if (points.length < 3) return 0;
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const next = points[(i + 1) % points.length];
    area += points[i].x * next.y - next.x * points[i].y;
  }
  return area / 2;
}

export function createShapeFromPoints(
  points: THREE.Vector2[],
): THREE.Shape | null {
  const cleanPoints = removeDuplicatePoints(points);
  if (
    cleanPoints.length < 3 ||
    Math.abs(signedPolygonArea(cleanPoints)) < MIN_POLYGON_AREA
  ) {
    return null;
  }
  return new THREE.Shape(cleanPoints);
}

export function getShapeEdgeRings(
  shape: THREE.Shape,
  segments = 48,
): THREE.Vector2[][] {
  const points = shape.extractPoints(segments);
  return [points.shape, ...points.holes]
    .map((ring) => removeDuplicatePoints(ring))
    .filter((ring) => ring.length >= 3);
}

export function createHorizontalShapeEdgeGeometry(
  shape: THREE.Shape,
  z: number,
  segments = 48,
): THREE.BufferGeometry {
  const positions: number[] = [];
  getShapeEdgeRings(shape, segments).forEach((ring) => {
    for (let index = 0; index < ring.length; index++) {
      const start = ring[index];
      const end = ring[(index + 1) % ring.length];
      positions.push(start.x, start.y, z, end.x, end.y, z);
    }
  });
  return createLineSegmentsGeometry(positions);
}

export function getCeilingHeightAtPoint(
  point: THREE.Vector2,
  walls: WallType[],
  endpointHeightMap: Map<string, WallEndpointHeights>,
  wallIds?: string[],
  fallback = DEFAULT_WALL_HEIGHT,
): number {
  const allowedWallIds = wallIds ? new Set(wallIds) : null;
  let closestWall: WallType | null = null;
  let closestDistanceSq = Number.POSITIVE_INFINITY;

  for (const wall of walls) {
    if (allowedWallIds && !allowedWallIds.has(wall.id)) continue;
    const distanceSq = getDistanceSqToWall(wall, point);
    if (distanceSq < closestDistanceSq) {
      closestDistanceSq = distanceSq;
      closestWall = wall;
    }
  }

  if (!closestWall) return fallback;
  const endpointHeights = endpointHeightMap.get(closestWall.id) ?? {
    start: getWall3DHeight(closestWall, fallback),
    end: getWall3DHeight(closestWall, fallback),
  };
  return getWallHeightAtPoint(closestWall, endpointHeights, point);
}

export function createSlopedCeilingGeometry(
  shape: THREE.Shape,
  walls: WallType[],
  endpointHeightMap: Map<string, WallEndpointHeights>,
  options: {
    wallIds?: string[];
    thickness?: number;
    fallbackHeight?: number;
  } = {},
): THREE.BufferGeometry {
  const rings = getShapeEdgeRings(shape);
  const geometry = new THREE.BufferGeometry();
  if (rings.length === 0) return geometry;

  const [contour, ...holes] = rings;
  const faces = THREE.ShapeUtils.triangulateShape(contour, holes);
  const points = [contour, ...holes].flat();
  const thickness = Math.max(
    options.thickness ?? DEFAULT_FLOOR_THICKNESS,
    MIN_GEOMETRY_DEPTH,
  );
  const fallbackHeight = options.fallbackHeight ?? DEFAULT_WALL_HEIGHT;
  const heights = points.map((point) =>
    getCeilingHeightAtPoint(
      point,
      walls,
      endpointHeightMap,
      options.wallIds,
      fallbackHeight,
    ),
  );
  const positions: number[] = [];
  const bounds = new THREE.Box2().setFromPoints(points);
  const size = bounds.getSize(new THREE.Vector2());
  const uvs = points.flatMap((point) => [
    size.x > 1e-8 ? (point.x - bounds.min.x) / size.x : 0,
    size.y > 1e-8 ? (point.y - bounds.min.y) / size.y : 0,
  ]);
  points.forEach((point, index) => {
    positions.push(point.x, point.y, heights[index]);
  });
  points.forEach((point, index) => {
    positions.push(point.x, point.y, heights[index] + thickness);
  });

  const indices: number[] = [];
  const topOffset = points.length;
  faces.forEach(([a, b, c]) => {
    indices.push(a, c, b);
    indices.push(a + topOffset, b + topOffset, c + topOffset);
  });

  let ringOffset = 0;
  rings.forEach((ring) => {
    for (let index = 0; index < ring.length; index++) {
      const start = ringOffset + index;
      const end = ringOffset + ((index + 1) % ring.length);
      indices.push(start, end, end + topOffset);
      indices.push(start, end + topOffset, start + topOffset);
    }
    ringOffset += ring.length;
  });

  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute(
    "uv",
    new THREE.Float32BufferAttribute([...uvs, ...uvs], 2),
  );
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

export function createSlopedCeilingEdgeGeometry(
  shape: THREE.Shape,
  walls: WallType[],
  endpointHeightMap: Map<string, WallEndpointHeights>,
  wallIds?: string[],
  fallbackHeight = DEFAULT_WALL_HEIGHT,
): THREE.BufferGeometry {
  const positions: number[] = [];
  getShapeEdgeRings(shape).forEach((ring) => {
    for (let index = 0; index < ring.length; index++) {
      const start = ring[index];
      const end = ring[(index + 1) % ring.length];
      positions.push(
        start.x,
        start.y,
        getCeilingHeightAtPoint(
          start,
          walls,
          endpointHeightMap,
          wallIds,
          fallbackHeight,
        ),
        end.x,
        end.y,
        getCeilingHeightAtPoint(
          end,
          walls,
          endpointHeightMap,
          wallIds,
          fallbackHeight,
        ),
      );
    }
  });
  return createLineSegmentsGeometry(positions);
}

function getDistanceSqToWall(wall: WallType, point: THREE.Vector2): number {
  const samples = sampleWallReferencePoints(
    wall,
    wall.lineForm === "arc" && wall.controlPoint ? 32 : 1,
  );
  let closestDistanceSq = Number.POSITIVE_INFINITY;
  for (let index = 0; index < samples.length - 1; index++) {
    const start = samples[index];
    const segment = samples[index + 1].clone().sub(start);
    const lengthSq = segment.lengthSq();
    const progress =
      lengthSq > 1e-12
        ? clamp(point.clone().sub(start).dot(segment) / lengthSq, 0, 1)
        : 0;
    const closest = start.clone().addScaledVector(segment, progress);
    closestDistanceSq = Math.min(
      closestDistanceSq,
      closest.distanceToSquared(point),
    );
  }
  return closestDistanceSq;
}

export function createVerticalShapeEdgeGeometry(
  shape: THREE.Shape,
  height: number,
  baseZ = 0,
  segments = 48,
): THREE.BufferGeometry {
  const topZ = baseZ + Math.max(height, MIN_GEOMETRY_DEPTH);
  const positions: number[] = [];

  getShapeEdgeRings(shape, segments).forEach((ring) => {
    for (let index = 0; index < ring.length; index++) {
      const start = ring[index];
      const end = ring[(index + 1) % ring.length];
      positions.push(start.x, start.y, baseZ, end.x, end.y, baseZ);
      positions.push(start.x, start.y, topZ, end.x, end.y, topZ);
      positions.push(start.x, start.y, baseZ, start.x, start.y, topZ);
    }
  });

  return createLineSegmentsGeometry(positions);
}

function createLineSegmentsGeometry(positions: number[]): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.computeBoundingSphere();
  return geometry;
}

export function buildWallExtrudeShapes(
  wallShape: WallShapeType | undefined,
  clippingPolygons: ClippingPolygonType[] | undefined,
  lineWeight: number,
  wallWidth: number,
): THREE.Shape[] {
  if (!wallShape) return [];
  const shapes = createShapes(wallShape, "inner", lineWeight, wallWidth);
  if (clippingPolygons?.length) {
    applyClippingPolygonsToShapes(shapes, clippingPolygons, wallShape.wall.id);
  }
  return shapes.filter((shape) => shape.getPoints().length >= 3);
}

export function createStraightWallOpeningGeometry(
  wall: WallType,
  symbols: SymbolType[],
  options: StraightWallOpeningGeometryOptions,
): THREE.BufferGeometry | null {
  if (!isStraightWallOpeningGeometrySupported(wall)) return null;

  const wallFrameBase = getLocalWallFrame(
    wall,
    options.wallShape,
    options.wallWidth,
  );
  if (!wallFrameBase) return null;

  let wallFrame = wallFrameBase;
  if (options.xRange) {
    const clampedMin = Math.max(wallFrameBase.xMin, options.xRange.min);
    const clampedMax = Math.min(wallFrameBase.xMax, options.xRange.max);
    if (clampedMax - clampedMin <= MIN_OPENING_SPAN) return null;
    wallFrame = {
      ...wallFrameBase,
      xMin: clampedMin,
      xMax: clampedMax,
    };
  }

  const height = Math.max(options.wallHeight, MIN_GEOMETRY_DEPTH);
  const symbolOpenings = getStraightWallOpenings(wall, symbols, wallFrame, height, {
    defaultDoorFrameWidth: options.defaultDoorFrameWidth,
    defaultWindowFrameWidth: options.defaultWindowFrameWidth,
  });
  /**
   * Neighbor clips must not become Shape holes that overlap symbol openings
   * (THREE.Shape triangulation fails on overlapping holes). Keep arched voids
   * intact and only cut junction stubs in the remaining X ranges.
   */
  const wallClipOpenings = mergeFullHeightOpenings(
    getWallClippingOpenings(
      wall,
      options.clippingPolygons,
      wallFrame,
      height,
    ),
  ).flatMap((clip) => subtractOpeningXRanges(clip, symbolOpenings));
  const openings = [...symbolOpenings, ...wallClipOpenings];
  const xBreaks = collectBreaks(
    [wallFrame.xMin, wallFrame.xMax],
    openings.flatMap((opening) => getOpeningXBreaks(opening)),
  );
  const zBreaks = collectBreaks(
    [0, height],
    openings.flatMap((opening) => getOpeningZBreaks(opening, height)),
  );
  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];

  const point = (x: number, y: number, z: number): THREE.Vector3 =>
    new THREE.Vector3(
      wall.start.x + wallFrame.tangent.x * x + wallFrame.normal.x * y,
      wall.start.y + wallFrame.tangent.y * x + wallFrame.normal.y * y,
      z,
    );
  const normal = (x: number, y: number, z = 0): THREE.Vector3 =>
    new THREE.Vector3(
      wallFrame.tangent.x * x + wallFrame.normal.x * y,
      wallFrame.tangent.y * x + wallFrame.normal.y * y,
      z,
    ).normalize();

  const addQuad = (
    corners: [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3],
    faceNormal: THREE.Vector3,
    flip = false,
  ): void => {
    const startIndex = positions.length / 3;
    corners.forEach((corner) => {
      positions.push(corner.x, corner.y, corner.z);
      normals.push(faceNormal.x, faceNormal.y, faceNormal.z);
    });
    if (flip) {
      indices.push(startIndex, startIndex + 2, startIndex + 1);
      indices.push(startIndex, startIndex + 3, startIndex + 2);
    } else {
      indices.push(startIndex, startIndex + 1, startIndex + 2);
      indices.push(startIndex, startIndex + 2, startIndex + 3);
    }
  };

  const broadFaces = options.broadFaces ?? "both";
  const structureOption = options.includeStructure;
  const resolvedStructureParts = (() => {
    if (structureOption === false) {
      return {
        horizontal: false,
        endCaps: false as boolean | "start" | "end" | "both",
        openings: false,
      };
    }
    if (structureOption === true || structureOption === undefined) {
      return {
        horizontal: true,
        endCaps: "both" as boolean | "start" | "end" | "both",
        openings: true,
      };
    }
    return {
      horizontal: structureOption.horizontal === true,
      endCaps: structureOption.endCaps ?? false,
      openings: structureOption.openings === true,
    };
  })();
  // Wall3DMeshes always passes an explicit includeStructure value.
  const structureSide = options.structureSide ?? "both";
  const structureY =
    structureSide === "left"
      ? { yMin: 0, yMax: wallFrame.yMax }
      : structureSide === "right"
        ? { yMin: wallFrame.yMin, yMax: 0 }
        : { yMin: wallFrame.yMin, yMax: wallFrame.yMax };
  const structureFrame: LocalWallFrame = {
    ...wallFrame,
    yMin: structureY.yMin,
    yMax: structureY.yMax,
  };
  const includeHorizontal = resolvedStructureParts.horizontal;
  const includeOpenings = resolvedStructureParts.openings;
  const endCapsMode = resolvedStructureParts.endCaps;
  const includeStartCap =
    endCapsMode === true || endCapsMode === "both" || endCapsMode === "start";
  const includeEndCap =
    endCapsMode === true || endCapsMode === "both" || endCapsMode === "end";

  if (broadFaces === "left" || broadFaces === "both") {
    addBroadWallFaceWithOpeningHoles(
      wallFrame,
      openings,
      height,
      wallFrame.yMax,
      normal(0, 1),
      point,
      positions,
      normals,
      indices,
    );
  }
  if (broadFaces === "right" || broadFaces === "both") {
    addBroadWallFaceWithOpeningHoles(
      wallFrame,
      openings,
      height,
      wallFrame.yMin,
      normal(0, -1),
      point,
      positions,
      normals,
      indices,
    );
  }

  if (includeHorizontal) {
    addHorizontalFaces(
      xBreaks,
      openings,
      structureFrame,
      0,
      point,
      new THREE.Vector3(0, 0, -1),
      addQuad,
      (opening) => opening.zMin <= MIN_OPENING_SPAN,
      true,
    );
    addHorizontalFaces(
      xBreaks,
      openings,
      structureFrame,
      height,
      point,
      new THREE.Vector3(0, 0, 1),
      addQuad,
      (opening) => height - opening.zMax <= MIN_OPENING_SPAN,
    );
  }
  if (includeStartCap) {
    addEndCapFaces(
      zBreaks,
      openings,
      structureFrame,
      wallFrame.xMin,
      point,
      normal(-1, 0),
      addQuad,
      (opening) => opening.xMin <= wallFrame.xMin + MIN_OPENING_SPAN,
    );
  }
  if (includeEndCap) {
    addEndCapFaces(
      zBreaks,
      openings,
      structureFrame,
      wallFrame.xMax,
      point,
      normal(1, 0),
      addQuad,
      (opening) => opening.xMax >= wallFrame.xMax - MIN_OPENING_SPAN,
      true,
    );
  }

  if (includeOpenings) {
    openings.forEach((opening) => {
      const leftTop = getOpeningTopZ(opening, opening.xMin, height);
      const rightTop = getOpeningTopZ(opening, opening.xMax, height);
      addQuad(
        [
          point(opening.xMin, wallFrame.yMin, opening.zMin),
          point(opening.xMin, wallFrame.yMax, opening.zMin),
          point(opening.xMin, wallFrame.yMax, leftTop),
          point(opening.xMin, wallFrame.yMin, leftTop),
        ],
        normal(1, 0),
      );
      addQuad(
        [
          point(opening.xMax, wallFrame.yMax, opening.zMin),
          point(opening.xMax, wallFrame.yMin, opening.zMin),
          point(opening.xMax, wallFrame.yMin, rightTop),
          point(opening.xMax, wallFrame.yMax, rightTop),
        ],
        normal(-1, 0),
      );
      if (opening.zMin > MIN_OPENING_SPAN) {
        addQuad(
          [
            point(opening.xMin, wallFrame.yMin, opening.zMin),
            point(opening.xMax, wallFrame.yMin, opening.zMin),
            point(opening.xMax, wallFrame.yMax, opening.zMin),
            point(opening.xMin, wallFrame.yMax, opening.zMin),
          ],
          new THREE.Vector3(0, 0, 1),
        );
      }
      if (height - getOpeningMaxTopZ(opening, height) > MIN_OPENING_SPAN) {
        const openingXBreaks = getOpeningXBreaks(opening);
        for (let i = 0; i < openingXBreaks.length - 1; i++) {
          const x0 = openingXBreaks[i];
          const x1 = openingXBreaks[i + 1];
          const z0 = getOpeningTopZ(opening, x0, height);
          const z1 = getOpeningTopZ(opening, x1, height);
          const corners: [
            THREE.Vector3,
            THREE.Vector3,
            THREE.Vector3,
            THREE.Vector3,
          ] = [
            point(x0, wallFrame.yMax, z0),
            point(x1, wallFrame.yMax, z1),
            point(x1, wallFrame.yMin, z1),
            point(x0, wallFrame.yMin, z0),
          ];
          addQuad(corners, getQuadNormal(corners));
        }
      }
    });
  }

  if (positions.length === 0) return null;

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  geometry.setIndex(indices);
  applyWallTextureUVs(geometry, wall);
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

export function createJointShape(points: THREE.Vector2[]): THREE.Shape | null {
  return createShapeFromPoints(points);
}

export function createOpeningFootprintShape(
  wall: WallType,
  symbol: SymbolType,
  positionFromStart: number,
  defaultFrameWidth: number,
  wallWidth: number,
): THREE.Shape | null {
  if (
    !isSingleLeafDoorType(symbol) &&
    !isDoubleDoorType(symbol) &&
    !isWindowType(symbol) &&
    !isVoidType(symbol)
  ) {
    return null;
  }

  const insert = getSymbolInsertGeometry(
    wall,
    symbol,
    positionFromStart,
    defaultFrameWidth,
  );
  const normal = new THREE.Vector2(-insert.tangent.y, insert.tangent.x);
  const halfWallWidth = Math.max(wallWidth, 0.01) / 2;
  const points = [
    insert.pA.clone().addScaledVector(normal, halfWallWidth),
    insert.pB.clone().addScaledVector(normal, halfWallWidth),
    insert.pB.clone().addScaledVector(normal, -halfWallWidth),
    insert.pA.clone().addScaledVector(normal, -halfWallWidth),
  ];

  return createShapeFromPoints(points);
}

function getLocalWallFrame(
  wall: WallType,
  wallShape: WallShapeType | undefined,
  wallWidth: number,
): LocalWallFrame | null {
  const wallVector = new THREE.Vector2().subVectors(wall.end, wall.start);
  const wallLength = wallVector.length();
  if (wallLength <= 1e-8) return null;

  const tangent = wallVector.divideScalar(wallLength);
  const normal = new THREE.Vector2(-tangent.y, tangent.x);
  const projected = wallShape?.paths
    ?.filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
    .map((point) => {
      const local = point.clone().sub(wall.start);
      return {
        x: local.dot(tangent),
        y: local.dot(normal),
      };
    });

  if (projected && projected.length >= 4) {
    const xs = projected.map((point) => point.x);
    const ys = projected.map((point) => point.y);
    const xMin = Math.min(...xs);
    const xMax = Math.max(...xs);
    const yMin = Math.min(...ys);
    const yMax = Math.max(...ys);
    if (xMax - xMin > MIN_OPENING_SPAN && yMax - yMin > MIN_OPENING_SPAN) {
      return { tangent, normal, xMin, xMax, yMin, yMax };
    }
  }

  const halfWallWidth = Math.max(wallWidth, MIN_OPENING_SPAN) / 2;
  return {
    tangent,
    normal,
    xMin: 0,
    xMax: wallLength,
    yMin: -halfWallWidth,
    yMax: halfWallWidth,
  };
}

/**
 * Neighbor-wall footprint clips become full-height openings so connected
 * straight walls can keep integrated geometry (and toppbåge) instead of
 * falling back to a rectangular extrusion.
 */
function getWallClippingOpenings(
  wall: WallType,
  clippingPolygons: ClippingPolygonType[] | undefined,
  wallFrame: LocalWallFrame,
  wallHeight: number,
): StraightWallOpening[] {
  if (!clippingPolygons?.length) return [];

  return clippingPolygons.flatMap((clipping) => {
    if (clipping.objectType !== "wall") return [];

    const projected = clipping.polygon
      .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
      .map((point) => {
        const local = point.clone().sub(wall.start);
        return {
          x: local.dot(wallFrame.tangent),
          y: local.dot(wallFrame.normal),
        };
      });
    if (projected.length < 3) return [];

    const xMin = clamp(
      Math.min(...projected.map((point) => point.x)),
      wallFrame.xMin,
      wallFrame.xMax,
    );
    const xMax = clamp(
      Math.max(...projected.map((point) => point.x)),
      wallFrame.xMin,
      wallFrame.xMax,
    );
    const yMin = clamp(
      Math.min(...projected.map((point) => point.y)),
      wallFrame.yMin,
      wallFrame.yMax,
    );
    const yMax = clamp(
      Math.max(...projected.map((point) => point.y)),
      wallFrame.yMin,
      wallFrame.yMax,
    );
    if (
      xMax - xMin <= MIN_OPENING_SPAN ||
      yMax - yMin <= MIN_OPENING_SPAN
    ) {
      return [];
    }

    return [
      {
        xMin,
        xMax,
        zMin: 0,
        zMax: wallHeight,
      },
    ];
  });
}

/**
 * Merge overlapping full-height wall-clip openings along X so duplicate
 * junction clips (renderStore + connected footprint) become one hole.
 */
function mergeFullHeightOpenings(
  openings: StraightWallOpening[],
): StraightWallOpening[] {
  if (openings.length <= 1) return openings;

  const sorted = [...openings].sort((a, b) => a.xMin - b.xMin || a.xMax - b.xMax);
  const merged: StraightWallOpening[] = [];
  for (const opening of sorted) {
    const last = merged[merged.length - 1];
    if (
      last &&
      opening.xMin <= last.xMax + MIN_OPENING_SPAN &&
      Math.abs(opening.zMin - last.zMin) <= 1e-8 &&
      Math.abs(opening.zMax - last.zMax) <= 1e-8 &&
      !hasCurvedTop(opening) &&
      !hasCurvedTop(last)
    ) {
      last.xMax = Math.max(last.xMax, opening.xMax);
      continue;
    }
    merged.push({ ...opening });
  }
  return merged;
}

/**
 * Remove X ranges covered by other openings so Shape holes never overlap.
 * Returns zero or more remnants of `opening` outside `blocked` spans.
 */
function subtractOpeningXRanges(
  opening: StraightWallOpening,
  blocked: StraightWallOpening[],
): StraightWallOpening[] {
  if (!blocked.length) return [opening];

  let segments: Array<{ xMin: number; xMax: number }> = [
    { xMin: opening.xMin, xMax: opening.xMax },
  ];

  for (const blocker of blocked) {
    const next: Array<{ xMin: number; xMax: number }> = [];
    for (const segment of segments) {
      if (
        blocker.xMax <= segment.xMin + MIN_OPENING_SPAN ||
        blocker.xMin >= segment.xMax - MIN_OPENING_SPAN
      ) {
        next.push(segment);
        continue;
      }
      if (blocker.xMin > segment.xMin + MIN_OPENING_SPAN) {
        next.push({
          xMin: segment.xMin,
          xMax: Math.min(segment.xMax, blocker.xMin),
        });
      }
      if (blocker.xMax < segment.xMax - MIN_OPENING_SPAN) {
        next.push({
          xMin: Math.max(segment.xMin, blocker.xMax),
          xMax: segment.xMax,
        });
      }
    }
    segments = next;
    if (!segments.length) break;
  }

  return segments
    .filter((segment) => segment.xMax - segment.xMin > MIN_OPENING_SPAN)
    .map((segment) => ({
      ...opening,
      xMin: segment.xMin,
      xMax: segment.xMax,
    }));
}

function getStraightWallOpenings(
  wall: WallType,
  symbols: SymbolType[],
  wallFrame: LocalWallFrame,
  wallHeight: number,
  options: {
    defaultDoorFrameWidth: number;
    defaultWindowFrameWidth: number;
  },
): StraightWallOpening[] {
  const symbolsById = new Map(symbols.map((symbol) => [symbol.id, symbol]));
  return (wall.symbolAttachments ?? [])
    .map((attachment): StraightWallOpening | null => {
      const symbol = symbolsById.get(attachment.symbolId);
      if (!symbol) return null;

      const defaultFrameWidth = isWindowType(symbol)
        ? options.defaultWindowFrameWidth
        : options.defaultDoorFrameWidth;
      const insert = getSymbolInsertGeometry(
        wall,
        symbol,
        attachment.positionFromStart,
        defaultFrameWidth,
      );
      const xA = insert.pA.clone().sub(wall.start).dot(wallFrame.tangent);
      const xB = insert.pB.clone().sub(wall.start).dot(wallFrame.tangent);
      const xMin = clamp(Math.min(xA, xB), wallFrame.xMin, wallFrame.xMax);
      const xMax = clamp(Math.max(xA, xB), wallFrame.xMin, wallFrame.xMax);
      const zRange = getOpeningZRange(symbol, wallHeight);
      if (
        !zRange ||
        xMax - xMin <= MIN_OPENING_SPAN ||
        zRange.zMax - zRange.zMin <= MIN_OPENING_SPAN
      ) {
        return null;
      }

      return {
        xMin,
        xMax,
        zMin: zRange.zMin,
        zMax: zRange.zMax,
        topBulge: getOpeningTopBulge(symbol, wallHeight),
      };
    })
    .filter((opening): opening is StraightWallOpening => opening !== null)
    .sort((a, b) => a.xMin - b.xMin || a.zMin - b.zMin);
}

function getOpeningZRange(
  symbol: SymbolType,
  wallHeight: number,
): { zMin: number; zMax: number } | null {
  if (isSingleLeafDoorType(symbol) || isDoubleDoorType(symbol)) {
    return {
      zMin: 0,
      zMax: clamp(symbol.threeD?.height ?? DEFAULT_DOOR_HEIGHT, 0, wallHeight),
    };
  }

  if (isWindowType(symbol)) {
    const zMin = clamp(
      getSymbol3DBaseElevation(symbol, DEFAULT_WINDOW_SILL_HEIGHT),
      0,
      wallHeight,
    );
    const zMax = clamp(
      zMin + (symbol.threeD?.height ?? DEFAULT_WINDOW_HEIGHT),
      0,
      wallHeight,
    );
    return { zMin, zMax };
  }

  if (isVoidType(symbol)) {
    const zMin = clamp(getSymbol3DBaseElevation(symbol), 0, wallHeight);
    const zMax = clamp(
      zMin + (symbol.threeD?.height ?? wallHeight - zMin),
      0,
      wallHeight,
    );
    return { zMin, zMax };
  }

  return null;
}

function getOpeningTopBulge(
  symbol: SymbolType,
  wallHeight: number,
): number | undefined {
  if (!isVoidType(symbol)) return undefined;
  const topBulge = symbol.threeD?.topBulge;
  if (!Number.isFinite(topBulge) || typeof topBulge !== "number") {
    return undefined;
  }
  return clamp(topBulge, -wallHeight, wallHeight);
}

function getOpeningXBreaks(opening: StraightWallOpening): number[] {
  if (!hasCurvedTop(opening)) return [opening.xMin, opening.xMax];
  const breaks: number[] = [];
  for (let i = 0; i <= CURVED_OPENING_SEGMENTS; i++) {
    const t = i / CURVED_OPENING_SEGMENTS;
    breaks.push(THREE.MathUtils.lerp(opening.xMin, opening.xMax, t));
  }
  return breaks;
}

function getOpeningZBreaks(
  opening: StraightWallOpening,
  wallHeight: number,
): number[] {
  if (!hasCurvedTop(opening)) return [opening.zMin, opening.zMax];
  return [
    opening.zMin,
    ...getOpeningXBreaks(opening).map((x) =>
      getOpeningTopZ(opening, x, wallHeight),
    ),
  ];
}

function hasCurvedTop(opening: StraightWallOpening): boolean {
  return Math.abs(opening.topBulge ?? 0) > 1e-6;
}

function getOpeningTopZ(
  opening: StraightWallOpening,
  x: number,
  wallHeight: number,
): number {
  const span = opening.xMax - opening.xMin;
  if (span <= MIN_OPENING_SPAN || !hasCurvedTop(opening)) {
    return opening.zMax;
  }
  const t = clamp((x - opening.xMin) / span, 0, 1);
  const curveFactor = 4 * t * (1 - t);
  return clamp(
    opening.zMax + (opening.topBulge ?? 0) * curveFactor,
    opening.zMin,
    wallHeight,
  );
}

function getOpeningMaxTopZ(
  opening: StraightWallOpening,
  wallHeight: number,
): number {
  if (!hasCurvedTop(opening)) return opening.zMax;
  return Math.max(
    ...getOpeningXBreaks(opening).map((x) =>
      getOpeningTopZ(opening, x, wallHeight),
    ),
  );
}

function collectBreaks(base: number[], values: number[]): number[] {
  return Array.from(
    new Set([...base, ...values].map((value) => roundBreak(value))),
  )
    .sort((a, b) => a - b)
    .filter(
      (value, index, all) => index === 0 || value - all[index - 1] > 1e-7,
    );
}

function roundBreak(value: number): number {
  return Math.round(value * 1e8) / 1e8;
}

function isInsideOpening(
  openings: StraightWallOpening[],
  x: number,
  z: number,
  wallHeight: number,
): boolean {
  return openings.some(
    (opening) =>
      x > opening.xMin + 1e-8 &&
      x < opening.xMax - 1e-8 &&
      z > opening.zMin + 1e-8 &&
      z < getOpeningTopZ(opening, x, wallHeight) - 1e-8,
  );
}

function addBroadWallFaceWithOpeningHoles(
  wallFrame: LocalWallFrame,
  openings: StraightWallOpening[],
  wallHeight: number,
  y: number,
  faceNormal: THREE.Vector3,
  point: (x: number, y: number, z: number) => THREE.Vector3,
  positions: number[],
  normals: number[],
  indices: number[],
): void {
  const shape = new THREE.Shape([
    new THREE.Vector2(wallFrame.xMin, 0),
    new THREE.Vector2(wallFrame.xMax, 0),
    new THREE.Vector2(wallFrame.xMax, wallHeight),
    new THREE.Vector2(wallFrame.xMin, wallHeight),
  ]);

  openings.forEach((opening) => {
    shape.holes.push(createOpeningHolePath(opening, wallHeight));
  });

  const geometry = new THREE.ShapeGeometry(shape);
  const position = geometry.getAttribute("position");
  const geometryIndex = geometry.getIndex();
  if (!geometryIndex) {
    geometry.dispose();
    return;
  }

  for (let i = 0; i < geometryIndex.count; i += 3) {
    const corners = [0, 1, 2].map((offset) => {
      const vertexIndex = geometryIndex.getX(i + offset);
      return point(position.getX(vertexIndex), y, position.getY(vertexIndex));
    }) as [THREE.Vector3, THREE.Vector3, THREE.Vector3];

    const triangleNormal = new THREE.Vector3()
      .subVectors(corners[1], corners[0])
      .cross(new THREE.Vector3().subVectors(corners[2], corners[0]))
      .normalize();
    if (triangleNormal.dot(faceNormal) < 0) {
      [corners[1], corners[2]] = [corners[2], corners[1]];
    }

    const startIndex = positions.length / 3;
    corners.forEach((corner) => {
      positions.push(corner.x, corner.y, corner.z);
      normals.push(faceNormal.x, faceNormal.y, faceNormal.z);
    });
    indices.push(startIndex, startIndex + 1, startIndex + 2);
  }

  geometry.dispose();
}

function createOpeningHolePath(
  opening: StraightWallOpening,
  wallHeight: number,
): THREE.Path {
  const points: THREE.Vector2[] = [
    new THREE.Vector2(opening.xMin, opening.zMin),
    new THREE.Vector2(opening.xMax, opening.zMin),
  ];
  const topBreaks = getOpeningXBreaks(opening);
  for (let i = topBreaks.length - 1; i >= 0; i--) {
    const x = topBreaks[i];
    points.push(new THREE.Vector2(x, getOpeningTopZ(opening, x, wallHeight)));
  }
  return new THREE.Path(points);
}

function getQuadNormal(
  corners: [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3],
): THREE.Vector3 {
  const normal = new THREE.Vector3()
    .subVectors(corners[1], corners[0])
    .cross(new THREE.Vector3().subVectors(corners[2], corners[0]))
    .normalize();
  return normal.lengthSq() > 0 ? normal : new THREE.Vector3(0, 0, -1);
}

function addHorizontalFaces(
  xBreaks: number[],
  openings: StraightWallOpening[],
  wallFrame: LocalWallFrame,
  z: number,
  point: (x: number, y: number, z: number) => THREE.Vector3,
  faceNormal: THREE.Vector3,
  addQuad: (
    corners: [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3],
    faceNormal: THREE.Vector3,
    flip?: boolean,
  ) => void,
  isOpenAtPlane: (opening: StraightWallOpening) => boolean,
  flip = false,
): void {
  for (let xi = 0; xi < xBreaks.length - 1; xi++) {
    const x0 = xBreaks[xi];
    const x1 = xBreaks[xi + 1];
    if (x1 - x0 <= MIN_OPENING_SPAN) continue;
    const xMid = (x0 + x1) / 2;
    const openingAtPlane = openings.some(
      (opening) =>
        isOpenAtPlane(opening) &&
        xMid > opening.xMin + 1e-8 &&
        xMid < opening.xMax - 1e-8,
    );
    if (openingAtPlane) continue;

    addQuad(
      [
        point(x0, wallFrame.yMin, z),
        point(x1, wallFrame.yMin, z),
        point(x1, wallFrame.yMax, z),
        point(x0, wallFrame.yMax, z),
      ],
      faceNormal,
      flip,
    );
  }
}

function addEndCapFaces(
  zBreaks: number[],
  openings: StraightWallOpening[],
  wallFrame: LocalWallFrame,
  x: number,
  point: (x: number, y: number, z: number) => THREE.Vector3,
  faceNormal: THREE.Vector3,
  addQuad: (
    corners: [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3],
    faceNormal: THREE.Vector3,
    flip?: boolean,
  ) => void,
  isOpenAtEnd: (opening: StraightWallOpening) => boolean,
  flip = false,
): void {
  for (let zi = 0; zi < zBreaks.length - 1; zi++) {
    const z0 = zBreaks[zi];
    const z1 = zBreaks[zi + 1];
    if (z1 - z0 <= MIN_OPENING_SPAN) continue;
    const zMid = (z0 + z1) / 2;
    const openingAtEnd = openings.some(
      (opening) =>
        isOpenAtEnd(opening) &&
        zMid > opening.zMin + 1e-8 &&
        zMid < opening.zMax - 1e-8,
    );
    if (openingAtEnd) continue;

    addQuad(
      [
        point(x, wallFrame.yMin, z0),
        point(x, wallFrame.yMax, z0),
        point(x, wallFrame.yMax, z1),
        point(x, wallFrame.yMin, z1),
      ],
      faceNormal,
      flip,
    );
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function clampDoorOpenAngle(angle: number): number {
  if (!Number.isFinite(angle)) return 0;
  return Math.min(Math.max(angle, 0), MAX_DOOR_OPEN_ANGLE);
}

export const clampWindowOpenAngle = clampDoorOpenAngle;

export function createDoor3DAssemblyGeometry(
  wall: WallType,
  symbol: Door3DSymbol,
  positionFromStart: number,
  options: Door3DAssemblyOptions,
): Door3DAssemblyGeometry | null {
  if (!isSingleLeafDoorType(symbol) && !isDoubleDoorType(symbol)) return null;

  const frameWidth = Math.max(
    symbol.doorFrameWidth ?? options.defaultFrameWidth,
    MIN_DOOR_FRAME_WIDTH,
  );
  const insert = getSymbolInsertGeometry(
    wall,
    symbol,
    positionFromStart,
    options.defaultFrameWidth,
  );
  const openingWidth = Math.max(
    insert.pA.distanceTo(insert.pB),
    MIN_DOOR_LEAF_WIDTH + frameWidth * 2,
  );
  const leafSpan = Math.max(openingWidth - frameWidth * 2, MIN_DOOR_LEAF_WIDTH);
  const origin = insert.midpoint
    .clone()
    .sub(insert.tangent.clone().multiplyScalar(leafSpan / 2));
  const rotation = Math.atan2(insert.tangent.y, insert.tangent.x);
  const wallWidth = Math.max(options.wallWidth, MIN_DOOR_THICKNESS);
  const doorHeight = Math.max(
    options.doorHeight ?? symbol.threeD?.height ?? DEFAULT_DOOR_HEIGHT,
    frameWidth * 2,
  );
  const leafHeight = Math.max(doorHeight - frameWidth, doorHeight * 0.72);
  const thickness = Math.max(
    symbol.doorBladeThickness ?? options.defaultBladeThickness,
    MIN_DOOR_THICKNESS,
  );
  // Sliding doors store open fraction in openAngle (0–1); keep leaf geometry closed.
  // Leaf spans half the clear opening so a passage stays visible beside the panel.
  const openAngle = isSlidingDoorType(symbol)
    ? 0
    : clampDoorOpenAngle(symbol.openAngle);
  const swingSign: 1 | -1 = symbol.flipVertical ? -1 : 1;

  const leaves = isSlidingDoorType(symbol)
    ? [
        createDoor3DLeafGeometry({
          id: `${symbol.id}-leaf`,
          hingeX: symbol.flipHorizontal ? leafSpan : 0,
          closedSign: symbol.flipHorizontal ? -1 : 1,
          rotationSign: (symbol.flipHorizontal ? -swingSign : swingSign) as
            | 1
            | -1,
          openAngle: 0,
          width: Math.max(leafSpan / 2, MIN_DOOR_LEAF_WIDTH),
          thickness,
          height: leafHeight,
        }),
      ]
    : isDoorType(symbol)
    ? [
        createDoor3DLeafGeometry({
          id: `${symbol.id}-leaf`,
          hingeX: symbol.flipHorizontal ? leafSpan : 0,
          closedSign: symbol.flipHorizontal ? -1 : 1,
          rotationSign: (symbol.flipHorizontal ? -swingSign : swingSign) as
            | 1
            | -1,
          openAngle,
          width: leafSpan,
          thickness,
          height: leafHeight,
        }),
      ]
    : [
        createDoor3DLeafGeometry({
          id: `${symbol.id}-left-leaf`,
          hingeX: 0,
          closedSign: 1,
          rotationSign: swingSign,
          openAngle,
          width: leafSpan / 2,
          thickness,
          height: leafHeight,
        }),
        createDoor3DLeafGeometry({
          id: `${symbol.id}-right-leaf`,
          hingeX: leafSpan,
          closedSign: -1,
          rotationSign: -swingSign as 1 | -1,
          openAngle,
          width: leafSpan / 2,
          thickness,
          height: leafHeight,
        }),
      ];

  return {
    origin,
    rotation,
    openingWidth,
    frameWidth,
    wallWidth,
    doorHeight,
    leafHeight,
    leaves,
    frames: createDoor3DFrameGeometry(
      leafSpan,
      frameWidth,
      wallWidth,
      doorHeight,
    ),
    casings: createDoor3DCasingGeometry(
      leafSpan,
      frameWidth,
      wallWidth,
      doorHeight,
    ),
    stops: createDoor3DStopGeometry(
      leafSpan,
      frameWidth,
      wallWidth,
      doorHeight,
    ),
    miterGrooves: createDoor3DMiterGrooveGeometry(
      leafSpan,
      frameWidth,
      wallWidth,
      doorHeight,
    ),
  };
}

export function createWindow3DAssemblyGeometry(
  wall: WallType,
  symbol: WindowType,
  positionFromStart: number,
  options: Window3DAssemblyOptions,
): Window3DAssemblyGeometry | null {
  if (!isWindowType(symbol)) return null;

  const frameWidth = Math.max(
    symbol.windowFrameWidth ?? options.defaultFrameWidth,
    MIN_DOOR_FRAME_WIDTH,
  );
  const insert = getSymbolInsertGeometry(
    wall,
    symbol,
    positionFromStart,
    options.defaultFrameWidth,
  );
  const openingWidth = Math.max(
    insert.pA.distanceTo(insert.pB),
    frameWidth * 4,
  );
  const wallWidth = Math.max(options.wallWidth, MIN_DOOR_THICKNESS);
  const sillHeight = clamp(
    getSymbol3DBaseElevation(symbol, DEFAULT_WINDOW_SILL_HEIGHT),
    0,
    options.wallHeight,
  );
  const windowHeight = Math.max(
    Math.min(
      symbol.threeD?.height ?? DEFAULT_WINDOW_HEIGHT,
      options.wallHeight - sillHeight,
    ),
    frameWidth * 4,
  );
  const sashDepth = clamp(
    symbol.windowWidth || Math.min(wallWidth * 0.52, 0.1),
    MIN_DOOR_THICKNESS,
    Math.max(wallWidth - 0.035, MIN_DOOR_THICKNESS),
  );
  const origin = insert.pA.clone();
  const rotation = Math.atan2(insert.tangent.y, insert.tangent.x);
  const openAngle = clampWindowOpenAngle(symbol.openAngle ?? 0);
  const swingSign: 1 | -1 = symbol.flipVertical ? -1 : 1;
  const closedSign: 1 | -1 = symbol.flipHorizontal ? -1 : 1;
  const rotationSign = (symbol.flipHorizontal ? -swingSign : swingSign) as
    | 1
    | -1;
  const hingeX = symbol.flipHorizontal ? openingWidth : 0;

  return {
    origin,
    rotation,
    openingWidth,
    wallWidth,
    frameWidth,
    sillHeight,
    windowHeight,
    sashDepth,
    leaf: createWindow3DLeafGeometry({
      id: `${symbol.id}-sash`,
      hingeX,
      closedSign,
      rotationSign,
      openAngle,
      width: openingWidth,
      depth: sashDepth,
      height: windowHeight,
      baseZ: sillHeight,
      frameWidth,
    }),
    frames: createWindow3DRevealFrameGeometry(
      openingWidth,
      frameWidth,
      wallWidth,
      sillHeight,
      windowHeight,
    ),
    casings: createWindow3DCasingGeometry(
      openingWidth,
      frameWidth,
      wallWidth,
      sillHeight,
      windowHeight,
    ),
    miterGrooves: createWindow3DMiterGrooveGeometry(
      openingWidth,
      frameWidth,
      wallWidth,
      sillHeight,
      windowHeight,
    ),
  };
}

export function createFloorShapesFromRooms(
  rooms: RoomType[],
  getObjectsOfRoom: (room: RoomType) => (WallType | SingleLineType)[],
  getRoomVertices: (
    objects: (WallType | SingleLineType)[],
    side: "inner",
  ) => THREE.Vector2[],
): THREE.Shape[] {
  return rooms
    .map((room) => {
      const objects = getObjectsOfRoom(room);
      if (objects.length < 3) return null;
      return createShapeFromPoints(getRoomVertices(objects, "inner"));
    })
    .filter((shape): shape is THREE.Shape => shape !== null);
}

export function createFloorShapeDefinitionsFromAreaFills(
  symbols: SymbolType[],
  options: {
    roomSurfaceVerticesBySignature?: Map<string, THREE.Vector2[]>;
  } = {},
): Floor3DShapeDefinition[] {
  const definitions: Floor3DShapeDefinition[] = [];

  symbols.filter(isAreaFillSymbolType).forEach((symbol) => {
    const worldVertices = symbol.vertices.map((vertex) =>
      vertex.clone().add(symbol.position),
    );
    const selectionShape = createShapeFromPoints(worldVertices);
    if (!selectionShape) return;

    const roomSurfaceVertices = symbol.roomBinding?.roomSignature
      ? options.roomSurfaceVerticesBySignature?.get(
          symbol.roomBinding.roomSignature,
        )
      : undefined;
    const roomSurfaceShape =
      roomSurfaceVertices && roomSurfaceVertices.length >= 3
        ? createShapeFromPoints(roomSurfaceVertices)
        : null;

    definitions.push({
      id: symbol.id,
      shape: roomSurfaceShape ?? selectionShape,
      selectionShape,
      fillColor: symbol.fillColor,
      fillOpacity: symbol.fillOpacity,
      fillPattern: symbol.fillPattern,
      source: "areaFill",
      roomId: symbol.roomBinding?.roomId,
      roomSignature: symbol.roomBinding?.roomSignature,
      roomObjectIds: symbol.roomBinding
        ? [...symbol.roomBinding.objectIds]
        : undefined,
    });
  });

  return definitions;
}

export function getCeilingHeightForFloor(
  floor: Floor3DShapeDefinition,
  rooms: RoomType[],
  wallHeightById: Map<string, number>,
  fallback = DEFAULT_WALL_HEIGHT,
): number {
  const objectIds =
    floor.roomObjectIds && floor.roomObjectIds.length > 0
      ? floor.roomObjectIds
      : getRoomObjectIdsForFloor(floor, rooms);
  const wallHeights = objectIds
    .map((objectId) => wallHeightById.get(objectId))
    .filter(
      (height): height is number =>
        typeof height === "number" && Number.isFinite(height) && height > 0,
    );

  return wallHeights.length > 0 ? Math.max(...wallHeights) : fallback;
}

export function createCeilingShapeDefinitionsFromFloors(
  floors: Floor3DShapeDefinition[],
  rooms: RoomType[],
  wallHeightById: Map<string, number>,
  fallback = DEFAULT_WALL_HEIGHT,
): Ceiling3DShapeDefinition[] {
  return floors.map((floor) => ({
    id: `${floor.id}-ceiling`,
    floorId: floor.id,
    shape: floor.shape,
    height: getCeilingHeightForFloor(floor, rooms, wallHeightById, fallback),
    source: floor.source,
    roomObjectIds:
      floor.roomObjectIds && floor.roomObjectIds.length > 0
        ? [...floor.roomObjectIds]
        : getRoomObjectIdsForFloor(floor, rooms),
  }));
}

export function getHiddenHorizontalSurfaceIdsForCamera(
  surfaces: HorizontalSurfaceOcclusionDefinition[],
  cameraPosition: THREE.Vector3,
  cameraDirection: THREE.Vector3,
): Set<string> {
  if (surfaces.length === 0 || cameraDirection.lengthSq() <= 1e-8) {
    return new Set();
  }

  const direction = cameraDirection.clone().normalize();
  const minVerticalLook = 1e-4;
  const planeEpsilon = 0.04;

  return new Set(
    surfaces
      .filter((surface) => {
        if (surface.hideWhenCameraIs === "below") {
          return (
            cameraPosition.z < surface.z - planeEpsilon &&
            direction.z > minVerticalLook
          );
        } else if (
          cameraPosition.z <= surface.z + planeEpsilon ||
          direction.z >= -minVerticalLook
        ) {
          return false;
        }

        return true;
      })
      .map((surface) => surface.id),
  );
}

function getRoomObjectIdsForFloor(
  floor: Floor3DShapeDefinition,
  rooms: RoomType[],
): string[] {
  const boundRoom = floor.roomId
    ? rooms.find((room) => room.id === floor.roomId)
    : undefined;
  if (boundRoom) return boundRoom.objectIds;

  const containingRoom = rooms.find((room) =>
    isPointInsideShape(room.centroid, floor.shape),
  );
  return containingRoom?.objectIds ?? [];
}

function isPointInsideShape(point: THREE.Vector2, shape: THREE.Shape): boolean {
  const [outerRing, ...holes] = getShapeEdgeRings(shape);
  if (!outerRing || outerRing.length < 3) return false;

  return (
    isPointInsideRing(point, outerRing) &&
    !holes.some((hole) => isPointInsideRing(point, hole))
  );
}

function isPointInsideRing(
  point: THREE.Vector2,
  ring: THREE.Vector2[],
): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i];
    const b = ring[j];
    const intersects =
      a.y > point.y !== b.y > point.y &&
      point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

function createDoor3DLeafGeometry({
  id,
  hingeX,
  closedSign,
  rotationSign,
  openAngle,
  width,
  thickness,
  height,
}: {
  id: string;
  hingeX: number;
  closedSign: 1 | -1;
  rotationSign: 1 | -1;
  openAngle: number;
  width: number;
  thickness: number;
  height: number;
}): Door3DLeafGeometry {
  const handleInset = Math.min(
    Math.max(width * 0.12, 0.08),
    Math.max(width * 0.3, 0.08),
  );
  const handleX = closedSign * Math.max(width - handleInset, width * 0.65);
  const handleZ =
    DOOR_BOTTOM_GAP + Math.min(Math.max(height * 0.48, 0.85), height * 0.68);
  const handleOffsetY = thickness / 2 + Math.max(thickness * 0.45, 0.018);
  const barrelRadius = Math.max(Math.min(thickness * 0.38, 0.03), 0.014);
  const barrelHeight = Math.min(Math.max(height * 0.09, 0.14), 0.24);

  return {
    id,
    hinge: new THREE.Vector2(hingeX, 0),
    closedSign,
    rotationSign,
    rotation: rotationSign * openAngle,
    width,
    thickness,
    height,
    bottomGap: DOOR_BOTTOM_GAP,
    panelCenter: new THREE.Vector3(
      (closedSign * width) / 2,
      0,
      DOOR_BOTTOM_GAP + height / 2,
    ),
    handleCenters: [
      new THREE.Vector3(handleX, handleOffsetY, handleZ),
      new THREE.Vector3(handleX, -handleOffsetY, handleZ),
    ],
    angleHandleCenter: new THREE.Vector3(
      handleX,
      rotationSign * Math.max(thickness * 2.6, 0.14),
      handleZ,
    ),
    hingeBarrels: [0.22, 0.5, 0.78].map((heightFactor) => ({
      center: new THREE.Vector3(0, 0, DOOR_BOTTOM_GAP + height * heightFactor),
      radius: barrelRadius,
      height: barrelHeight,
    })),
  };
}

function createDoor3DFrameGeometry(
  leafSpan: number,
  frameWidth: number,
  wallWidth: number,
  doorHeight: number,
): Door3DFrameGeometry[] {
  const frameDepth = Math.max(wallWidth, frameWidth);
  return [
    {
      id: "left-jamb",
      center: new THREE.Vector3(-frameWidth / 2, 0, doorHeight / 2),
      size: new THREE.Vector3(frameWidth, frameDepth, doorHeight),
    },
    {
      id: "right-jamb",
      center: new THREE.Vector3(leafSpan + frameWidth / 2, 0, doorHeight / 2),
      size: new THREE.Vector3(frameWidth, frameDepth, doorHeight),
    },
    {
      id: "header",
      center: new THREE.Vector3(leafSpan / 2, 0, doorHeight - frameWidth / 2),
      size: new THREE.Vector3(
        leafSpan + frameWidth * 2,
        frameDepth,
        frameWidth,
      ),
    },
  ];
}

function createDoor3DCasingGeometry(
  leafSpan: number,
  frameWidth: number,
  wallWidth: number,
  doorHeight: number,
): Door3DBoxGeometry[] {
  const { casingWidth, casingDepth, beadWidth, beadDepth } =
    getDoorTrimMeasurements(frameWidth);
  const casings: Door3DBoxGeometry[] = [];

  [-1, 1].forEach((side) => {
    const faceY = side * (wallWidth / 2 + casingDepth / 2);
    const beadY = side * (wallWidth / 2 + casingDepth + beadDepth / 2);

    casings.push(
      {
        id: `casing-${side}-left`,
        center: new THREE.Vector3(-casingWidth / 2, faceY, doorHeight / 2),
        size: new THREE.Vector3(casingWidth, casingDepth, doorHeight),
      },
      {
        id: `casing-${side}-right`,
        center: new THREE.Vector3(
          leafSpan + casingWidth / 2,
          faceY,
          doorHeight / 2,
        ),
        size: new THREE.Vector3(casingWidth, casingDepth, doorHeight),
      },
      {
        id: `casing-${side}-header`,
        center: new THREE.Vector3(
          leafSpan / 2,
          faceY,
          doorHeight + casingWidth / 2,
        ),
        size: new THREE.Vector3(
          leafSpan + casingWidth * 2,
          casingDepth,
          casingWidth,
        ),
      },
      {
        id: `casing-${side}-left-inner-bead`,
        center: new THREE.Vector3(-beadWidth / 2, beadY, doorHeight / 2),
        size: new THREE.Vector3(beadWidth, beadDepth, doorHeight),
      },
      {
        id: `casing-${side}-right-inner-bead`,
        center: new THREE.Vector3(
          leafSpan + beadWidth / 2,
          beadY,
          doorHeight / 2,
        ),
        size: new THREE.Vector3(beadWidth, beadDepth, doorHeight),
      },
      {
        id: `casing-${side}-header-inner-bead`,
        center: new THREE.Vector3(
          leafSpan / 2,
          beadY,
          doorHeight + beadWidth / 2,
        ),
        size: new THREE.Vector3(leafSpan + beadWidth * 2, beadDepth, beadWidth),
      },
    );
  });

  return casings;
}

function createDoor3DStopGeometry(
  leafSpan: number,
  frameWidth: number,
  wallWidth: number,
  doorHeight: number,
): Door3DBoxGeometry[] {
  const stopWidth = clamp(frameWidth * 0.36, 0.014, 0.028);
  const stopDepth = clamp(wallWidth * 0.18, 0.022, 0.045);
  const stopY = wallWidth * 0.22;

  return [-1, 1].flatMap((side) => [
    {
      id: `stop-${side}-left`,
      center: new THREE.Vector3(stopWidth / 2, side * stopY, doorHeight / 2),
      size: new THREE.Vector3(stopWidth, stopDepth, doorHeight),
    },
    {
      id: `stop-${side}-right`,
      center: new THREE.Vector3(
        leafSpan - stopWidth / 2,
        side * stopY,
        doorHeight / 2,
      ),
      size: new THREE.Vector3(stopWidth, stopDepth, doorHeight),
    },
    {
      id: `stop-${side}-header`,
      center: new THREE.Vector3(
        leafSpan / 2,
        side * stopY,
        doorHeight - stopWidth / 2,
      ),
      size: new THREE.Vector3(leafSpan, stopDepth, stopWidth),
    },
  ]);
}

function createDoor3DMiterGrooveGeometry(
  leafSpan: number,
  frameWidth: number,
  wallWidth: number,
  doorHeight: number,
): Door3DBoxGeometry[] {
  const { casingWidth, casingDepth } = getDoorTrimMeasurements(frameWidth);
  const grooveDepth = 0.004;
  const grooveWidth = 0.006;
  const grooveLength = casingWidth * 1.35;

  return [-1, 1].flatMap((side) => {
    const grooveY = side * (wallWidth / 2 + casingDepth + grooveDepth / 2);
    return [
      {
        id: `miter-${side}-left`,
        center: new THREE.Vector3(
          -casingWidth * 0.46,
          grooveY,
          doorHeight + casingWidth * 0.46,
        ),
        size: new THREE.Vector3(grooveLength, grooveDepth, grooveWidth),
        rotationY: -Math.PI / 4,
      },
      {
        id: `miter-${side}-right`,
        center: new THREE.Vector3(
          leafSpan + casingWidth * 0.46,
          grooveY,
          doorHeight + casingWidth * 0.46,
        ),
        size: new THREE.Vector3(grooveLength, grooveDepth, grooveWidth),
        rotationY: Math.PI / 4,
      },
    ];
  });
}

function createWindow3DLeafGeometry({
  id,
  hingeX,
  closedSign,
  rotationSign,
  openAngle,
  width,
  depth,
  height,
  baseZ,
  frameWidth,
}: {
  id: string;
  hingeX: number;
  closedSign: 1 | -1;
  rotationSign: 1 | -1;
  openAngle: number;
  width: number;
  depth: number;
  height: number;
  baseZ: number;
  frameWidth: number;
}): Window3DLeafGeometry {
  const stileWidth = clamp(frameWidth * 1.15, 0.045, 0.09);
  const railWidth = stileWidth;
  const glassInsetWidth = Math.max(width - stileWidth * 2, width * 0.35);
  const glassInsetHeight = Math.max(height - railWidth * 2, height * 0.35);
  const handleZ =
    baseZ + Math.min(Math.max(height * 0.45, 0.48), height * 0.62);
  const handleOffsetY = depth / 2 + Math.max(depth * 0.32, 0.018);
  const barrelRadius = Math.max(Math.min(depth * 0.18, 0.022), 0.01);
  const barrelHeight = Math.min(Math.max(height * 0.12, 0.12), 0.2);
  const railCenterX = (closedSign * width) / 2;
  const handleX = closedSign * (width - stileWidth / 2);

  return {
    id,
    hinge: new THREE.Vector2(hingeX, 0),
    closedSign,
    rotationSign,
    rotation: rotationSign * openAngle,
    width,
    depth,
    height,
    baseZ,
    frameWidth: stileWidth,
    sashFrames: [
      {
        id: `${id}-hinge-stile`,
        center: new THREE.Vector3(
          (closedSign * stileWidth) / 2,
          0,
          baseZ + height / 2,
        ),
        size: new THREE.Vector3(stileWidth, depth, height),
      },
      {
        id: `${id}-handle-stile`,
        center: new THREE.Vector3(
          closedSign * (width - stileWidth / 2),
          0,
          baseZ + height / 2,
        ),
        size: new THREE.Vector3(stileWidth, depth, height),
      },
      {
        id: `${id}-top-rail`,
        center: new THREE.Vector3(
          railCenterX,
          0,
          baseZ + height - railWidth / 2,
        ),
        size: new THREE.Vector3(width, depth, railWidth),
      },
      {
        id: `${id}-bottom-rail`,
        center: new THREE.Vector3(railCenterX, 0, baseZ + railWidth / 2),
        size: new THREE.Vector3(width, depth, railWidth),
      },
    ],
    glassPane: {
      id: `${id}-glass`,
      center: new THREE.Vector3(railCenterX, 0, baseZ + height / 2),
      size: new THREE.Vector3(
        glassInsetWidth,
        Math.max(depth * 0.12, 0.01),
        glassInsetHeight,
      ),
    },
    handleCenters: [
      new THREE.Vector3(handleX, handleOffsetY, handleZ),
      new THREE.Vector3(handleX, -handleOffsetY, handleZ),
    ],
    angleHandleCenter: new THREE.Vector3(
      handleX,
      rotationSign * Math.max(depth * 1.5, 0.13),
      handleZ,
    ),
    hingeBarrels: [0.18, 0.5, 0.82].map((heightFactor) => ({
      center: new THREE.Vector3(0, 0, baseZ + height * heightFactor),
      radius: barrelRadius,
      height: barrelHeight,
    })),
  };
}

function createWindow3DRevealFrameGeometry(
  openingWidth: number,
  frameWidth: number,
  wallWidth: number,
  sillHeight: number,
  windowHeight: number,
): Window3DBoxGeometry[] {
  const revealWidth = clamp(frameWidth * 0.72, 0.03, 0.06);
  const revealDepth = Math.max(wallWidth * 0.82, revealWidth);
  return [
    {
      id: "reveal-left",
      center: new THREE.Vector3(
        revealWidth / 2,
        0,
        sillHeight + windowHeight / 2,
      ),
      size: new THREE.Vector3(revealWidth, revealDepth, windowHeight),
    },
    {
      id: "reveal-right",
      center: new THREE.Vector3(
        openingWidth - revealWidth / 2,
        0,
        sillHeight + windowHeight / 2,
      ),
      size: new THREE.Vector3(revealWidth, revealDepth, windowHeight),
    },
    {
      id: "reveal-top",
      center: new THREE.Vector3(
        openingWidth / 2,
        0,
        sillHeight + windowHeight - revealWidth / 2,
      ),
      size: new THREE.Vector3(openingWidth, revealDepth, revealWidth),
    },
    {
      id: "reveal-bottom",
      center: new THREE.Vector3(
        openingWidth / 2,
        0,
        sillHeight + revealWidth / 2,
      ),
      size: new THREE.Vector3(openingWidth, revealDepth, revealWidth),
    },
  ];
}

function createWindow3DCasingGeometry(
  openingWidth: number,
  frameWidth: number,
  wallWidth: number,
  sillHeight: number,
  windowHeight: number,
): Window3DBoxGeometry[] {
  const { casingWidth, casingDepth, beadWidth, beadDepth } =
    getDoorTrimMeasurements(frameWidth);
  const zCenter = sillHeight + windowHeight / 2;
  const topZ = sillHeight + windowHeight + casingWidth / 2;
  const bottomZ = sillHeight - casingWidth / 2;

  return [-1, 1].flatMap((side) => {
    const faceY = side * (wallWidth / 2 + casingDepth / 2);
    const beadY = side * (wallWidth / 2 + casingDepth + beadDepth / 2);

    return [
      {
        id: `window-casing-${side}-left`,
        center: new THREE.Vector3(-casingWidth / 2, faceY, zCenter),
        size: new THREE.Vector3(
          casingWidth,
          casingDepth,
          windowHeight + casingWidth * 2,
        ),
      },
      {
        id: `window-casing-${side}-right`,
        center: new THREE.Vector3(
          openingWidth + casingWidth / 2,
          faceY,
          zCenter,
        ),
        size: new THREE.Vector3(
          casingWidth,
          casingDepth,
          windowHeight + casingWidth * 2,
        ),
      },
      {
        id: `window-casing-${side}-top`,
        center: new THREE.Vector3(openingWidth / 2, faceY, topZ),
        size: new THREE.Vector3(
          openingWidth + casingWidth * 2,
          casingDepth,
          casingWidth,
        ),
      },
      {
        id: `window-casing-${side}-bottom`,
        center: new THREE.Vector3(openingWidth / 2, faceY, bottomZ),
        size: new THREE.Vector3(
          openingWidth + casingWidth * 2,
          casingDepth,
          casingWidth,
        ),
      },
      {
        id: `window-casing-${side}-left-inner-bead`,
        center: new THREE.Vector3(-beadWidth / 2, beadY, zCenter),
        size: new THREE.Vector3(beadWidth, beadDepth, windowHeight),
      },
      {
        id: `window-casing-${side}-right-inner-bead`,
        center: new THREE.Vector3(openingWidth + beadWidth / 2, beadY, zCenter),
        size: new THREE.Vector3(beadWidth, beadDepth, windowHeight),
      },
      {
        id: `window-casing-${side}-top-inner-bead`,
        center: new THREE.Vector3(
          openingWidth / 2,
          beadY,
          sillHeight + windowHeight + beadWidth / 2,
        ),
        size: new THREE.Vector3(
          openingWidth + beadWidth * 2,
          beadDepth,
          beadWidth,
        ),
      },
      {
        id: `window-casing-${side}-bottom-inner-bead`,
        center: new THREE.Vector3(
          openingWidth / 2,
          beadY,
          sillHeight - beadWidth / 2,
        ),
        size: new THREE.Vector3(
          openingWidth + beadWidth * 2,
          beadDepth,
          beadWidth,
        ),
      },
    ];
  });
}

function createWindow3DMiterGrooveGeometry(
  openingWidth: number,
  frameWidth: number,
  wallWidth: number,
  sillHeight: number,
  windowHeight: number,
): Window3DBoxGeometry[] {
  const { casingWidth, casingDepth } = getDoorTrimMeasurements(frameWidth);
  const grooveDepth = 0.004;
  const grooveWidth = 0.006;
  const grooveLength = casingWidth * 1.35;
  const topZ = sillHeight + windowHeight + casingWidth * 0.46;
  const bottomZ = sillHeight - casingWidth * 0.46;

  return [-1, 1].flatMap((side) => {
    const grooveY = side * (wallWidth / 2 + casingDepth + grooveDepth / 2);
    return [
      {
        id: `window-miter-${side}-top-left`,
        center: new THREE.Vector3(-casingWidth * 0.46, grooveY, topZ),
        size: new THREE.Vector3(grooveLength, grooveDepth, grooveWidth),
        rotationY: -Math.PI / 4,
      },
      {
        id: `window-miter-${side}-top-right`,
        center: new THREE.Vector3(
          openingWidth + casingWidth * 0.46,
          grooveY,
          topZ,
        ),
        size: new THREE.Vector3(grooveLength, grooveDepth, grooveWidth),
        rotationY: Math.PI / 4,
      },
      {
        id: `window-miter-${side}-bottom-left`,
        center: new THREE.Vector3(-casingWidth * 0.46, grooveY, bottomZ),
        size: new THREE.Vector3(grooveLength, grooveDepth, grooveWidth),
        rotationY: Math.PI / 4,
      },
      {
        id: `window-miter-${side}-bottom-right`,
        center: new THREE.Vector3(
          openingWidth + casingWidth * 0.46,
          grooveY,
          bottomZ,
        ),
        size: new THREE.Vector3(grooveLength, grooveDepth, grooveWidth),
        rotationY: -Math.PI / 4,
      },
    ];
  });
}

function getDoorTrimMeasurements(frameWidth: number): {
  casingWidth: number;
  casingDepth: number;
  beadWidth: number;
  beadDepth: number;
} {
  const casingWidth = clamp(frameWidth * 1.55, 0.07, 0.13);
  const casingDepth = clamp(frameWidth * 0.34, 0.014, 0.03);
  const beadWidth = clamp(casingWidth * 0.24, 0.014, 0.028);
  const beadDepth = clamp(casingDepth * 0.46, 0.006, 0.014);
  return { casingWidth, casingDepth, beadWidth, beadDepth };
}

/**
 * Convex hull of wall sample points. Kept as a geometry helper; the 3D
 * scene must not use this as an automatic floor when there is no area fill.
 */
export function createConvexHullFloorShape(
  walls: WallType[],
): THREE.Shape | null {
  const points = walls.flatMap((wall) => sampleWallReferencePoints(wall));
  const hull = convexHull(points);
  return createShapeFromPoints(hull);
}

export function sampleWallReferencePoints(
  wall: WallType,
  segments = 16,
): THREE.Vector2[] {
  if (wall.lineForm !== "arc" || !wall.controlPoint) {
    return [wall.start.clone(), wall.end.clone()];
  }

  const samples: THREE.Vector2[] = [];
  for (let i = 0; i <= segments; i++) {
    samples.push(
      getQuadraticBezierPoint(
        i / segments,
        wall.start,
        wall.controlPoint,
        wall.end,
      ),
    );
  }
  return samples;
}

export function getPlanBoundsFromWalls(walls: WallType[]): THREE.Box2 {
  const bounds = new THREE.Box2();
  walls.forEach((wall) => {
    sampleWallReferencePoints(wall).forEach((point) =>
      bounds.expandByPoint(point),
    );
  });
  return bounds;
}

// Legacy name retained for callers: camera hiding is limited to exterior walls
// and fallback closed-outline walls.
export function getHiddenExteriorWallIdsForCamera(
  walls: WallType[],
  cameraPosition: THREE.Vector3,
  target: THREE.Vector3,
  rooms: RoomType[] = [],
  cameraDirection?: THREE.Vector3,
): Set<string> {
  if (walls.length === 0) {
    return new Set();
  }

  const roomById = new Map(rooms.map((room) => [room.id, room]));
  const roomsByWallId = getRoomsByWallId(rooms);
  const forward = getCameraForward2D(cameraPosition, target, cameraDirection);
  if (!forward) return new Set();

  const cameraHidableWallIds = getCameraHidableWallIds(walls, roomsByWallId);
  const occlusionEpsilon = 0.08;
  return new Set(
    walls
      .filter((wall) => {
        if (!cameraHidableWallIds.has(wall.id)) return false;

        const wallRooms = getRoomsForWall(
          wall,
          roomById,
          roomsByWallId,
        );
        const interiorSides = getWallInteriorSideCandidates(
          wall,
          wallRooms,
          target,
        );
        if (hasRoomInteriorsOnOppositeWallSides(interiorSides)) {
          return false;
        }

        return interiorSides.some(
          ({ interiorNormal, interiorReference }) =>
            isCameraOnWallExteriorSide(
              wall,
              cameraPosition,
              interiorNormal,
            ) &&
            isCameraLookingThroughWallFacade(forward, interiorNormal) &&
            hasWallSpanInFrontOfInterior(
              wall,
              cameraPosition,
              forward,
              interiorReference,
              interiorNormal,
              occlusionEpsilon,
            ),
        );
      })
      .map((wall) => wall.id),
  );
}

function hasWallSpanInFrontOfInterior(
  wall: WallType,
  cameraPosition: THREE.Vector3,
  forward: THREE.Vector2,
  interiorReference: THREE.Vector3,
  interiorNormal: THREE.Vector2,
  occlusionEpsilon: number,
): boolean {
  const camera2 = new THREE.Vector2(cameraPosition.x, cameraPosition.y);
  const wallProjection = getWallProjectionIntervalFromCamera(
    wall,
    camera2,
    forward,
  );
  if (!wallProjection || wallProjection.max <= occlusionEpsilon) {
    return false;
  }

  const interiorReferenceDistance = new THREE.Vector2(
    interiorReference.x,
    interiorReference.y,
  )
    .sub(camera2)
    .dot(forward);
  const interiorProbeDistance = getWallInteriorProbe(wall, interiorNormal)
    .sub(camera2)
    .dot(forward);
  const interiorDistance = Math.max(
    interiorReferenceDistance,
    interiorProbeDistance,
  );

  return (
    interiorDistance > occlusionEpsilon &&
    wallProjection.min < interiorDistance - occlusionEpsilon
  );
}

function getWallProjectionIntervalFromCamera(
  wall: WallType,
  cameraPosition: THREE.Vector2,
  forward: THREE.Vector2,
): { min: number; max: number } | null {
  const samples = sampleWallReferencePoints(wall, 8);
  if (samples.length === 0) return null;

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  samples.forEach((point) => {
    const projection = point.clone().sub(cameraPosition).dot(forward);
    min = Math.min(min, projection);
    max = Math.max(max, projection);
  });

  return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : null;
}

function getWallInteriorProbe(
  wall: WallType,
  interiorNormal: THREE.Vector2,
): THREE.Vector2 {
  const center = wallCenter(wall);
  const wallWidth = wall.wallWidth ?? 0.2;
  const probeDepth = Math.max(0.25, wallWidth * 2);
  return center.add(interiorNormal.clone().multiplyScalar(probeDepth));
}

function getCameraForward2D(
  cameraPosition: THREE.Vector3,
  target: THREE.Vector3,
  cameraDirection?: THREE.Vector3,
): THREE.Vector2 | null {
  const forward = cameraDirection
    ? new THREE.Vector2(cameraDirection.x, cameraDirection.y)
    : new THREE.Vector2(
        target.x - cameraPosition.x,
        target.y - cameraPosition.y,
      );

  return forward.lengthSq() > 1e-8 ? forward.normalize() : null;
}

function getWallInteriorReference(
  room: RoomType | undefined,
  fallback: THREE.Vector3,
): THREE.Vector3 {
  if (room?.centroid) {
    return new THREE.Vector3(room.centroid.x, room.centroid.y, fallback.z);
  }

  return fallback;
}

function getRoomsByWallId(rooms: RoomType[]): Map<string, RoomType[]> {
  const roomsByWallId = new Map<string, RoomType[]>();
  rooms.forEach((room) => {
    room.objectIds.forEach((objectId) => {
      const wallRooms = roomsByWallId.get(objectId) ?? [];
      wallRooms.push(room);
      roomsByWallId.set(objectId, wallRooms);
    });
  });
  return roomsByWallId;
}

function getRoomsForWall(
  wall: WallType,
  roomById: Map<string, RoomType>,
  roomsByWallId: Map<string, RoomType[]>,
): (RoomType | undefined)[] {
  const rooms: (RoomType | undefined)[] = [];
  const seenRoomIds = new Set<string>();

  (wall.roomIds ?? []).forEach((roomId) => {
    seenRoomIds.add(roomId);
    rooms.push(roomById.get(roomId));
  });

  roomsByWallId.get(wall.id)?.forEach((room) => {
    if (seenRoomIds.has(room.id)) return;
    seenRoomIds.add(room.id);
    rooms.push(room);
  });

  return rooms;
}

/**
 * Candidate room-facing sides for a wall.
 *
 * Exterior walls usually produce one side; partition walls produce one side per
 * adjacent room. Callers can reject walls with room candidates on both sides
 * when only exterior walls should be camera-hidden.
 * The per-room side uses a point-in-polygon test against the room's stored
 * inner polygon, which stays correct for non-convex rooms where the
 * area-weighted centroid can land on a wall's exterior side. Lacking usable
 * room geometry we fall back to the camera visibility target.
 */
function getWallInteriorSideCandidates(
  wall: WallType,
  rooms: (RoomType | undefined)[],
  fallback: THREE.Vector3,
): WallInteriorSideCandidate[] {
  const tangent = getWallReferenceTangent(wall);
  if (!tangent || tangent.lengthSq() < 1e-8) return [];
  const normal = new THREE.Vector2(-tangent.y, tangent.x).normalize();

  const candidates: WallInteriorSideCandidate[] = [];
  for (const room of rooms) {
    const roomNormal = getWallInteriorNormalForRoom(wall, room, normal);
    if (!roomNormal) continue;
    candidates.push({
      interiorNormal: roomNormal,
      interiorReference: getWallInteriorReference(room, fallback),
      source: "room",
    });
  }

  if (candidates.length > 0) return candidates;

  // No usable room geometry: derive the interior side from the camera target.
  const center = wallCenter(wall);
  const interiorSide = new THREE.Vector2(fallback.x, fallback.y)
    .sub(center)
    .dot(normal);
  if (Math.abs(interiorSide) < 1e-8) return [];
  return [
    {
      interiorNormal: interiorSide > 0 ? normal : normal.clone().negate(),
      interiorReference: fallback,
      source: "fallback",
    },
  ];
}

function hasRoomInteriorsOnOppositeWallSides(
  candidates: WallInteriorSideCandidate[],
): boolean {
  const roomCandidates = candidates.filter(
    (candidate) => candidate.source === "room",
  );
  for (let i = 0; i < roomCandidates.length; i++) {
    for (let j = i + 1; j < roomCandidates.length; j++) {
      if (
        roomCandidates[i].interiorNormal.dot(
          roomCandidates[j].interiorNormal,
        ) < -0.5
      ) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Unit normal pointing toward a single room across the wall, or null when the
 * room's side cannot be determined. Prefers the room polygon and falls back to
 * the room centroid.
 */
function getWallInteriorNormalForRoom(
  wall: WallType,
  room: RoomType | undefined,
  normal: THREE.Vector2,
): THREE.Vector2 | null {
  if (!room) return null;

  const polygonNormal = getWallInteriorNormalFromRoomPolygon(
    wall,
    room,
    normal,
  );
  if (polygonNormal) return polygonNormal;

  if (!room.centroid) return null;
  const center = wallCenter(wall);
  const side = room.centroid.clone().sub(center).dot(normal);
  if (Math.abs(side) < 1e-8) return null;
  return side > 0 ? normal : normal.clone().negate();
}

function getWallInteriorNormalFromRoomPolygon(
  wall: WallType,
  room: RoomType | undefined,
  normal: THREE.Vector2,
): THREE.Vector2 | null {
  const vertices = room?.vertices;
  if (!vertices || vertices.length < 3) return null;

  // The room polygon is the inner (room-facing) face, offset inward from the
  // wall centerline by half the wall width. Probe just past that inner edge on
  // each side; only the interior side lands inside the polygon.
  const center = wallCenter(wall);
  const halfWidth = (wall.wallWidth ?? 0.2) / 2;
  const probeDepth = halfWidth + Math.max(0.05, halfWidth);
  const positiveInside = isPointInPolygon(
    center.clone().add(normal.clone().multiplyScalar(probeDepth)),
    vertices,
  );
  const negativeInside = isPointInPolygon(
    center.clone().add(normal.clone().multiplyScalar(-probeDepth)),
    vertices,
  );
  if (positiveInside === negativeInside) return null; // inconclusive
  return positiveInside ? normal : normal.clone().negate();
}

function isCameraOnWallExteriorSide(
  wall: WallType,
  cameraPosition: THREE.Vector3,
  interiorNormal: THREE.Vector2,
): boolean {
  const center = wallCenter(wall);
  const cameraSide = new THREE.Vector2(
    cameraPosition.x - center.x,
    cameraPosition.y - center.y,
  ).dot(interiorNormal);
  const sideEpsilon = 0.02;

  // Camera is on the exterior side when it sits opposite the interior normal.
  return cameraSide < -sideEpsilon;
}

function isCameraLookingThroughWallFacade(
  forward: THREE.Vector2,
  interiorNormal: THREE.Vector2,
): boolean {
  const facadeFacingEpsilon = 0.12;
  return forward.dot(interiorNormal) > facadeFacingEpsilon;
}

function getCameraHidableWallIds(
  walls: WallType[],
  roomsByWallId: Map<string, RoomType[]>,
): Set<string> {
  const hidable = new Set<string>();
  walls.forEach((wall) => {
    if ((wall.roomIds?.length ?? 0) > 0 || roomsByWallId.has(wall.id)) {
      hidable.add(wall.id);
    }
  });

  getClosedEndpointLoopWallIds(walls).forEach((wallId) =>
    hidable.add(wallId),
  );
  return hidable;
}

function getClosedEndpointLoopWallIds(walls: WallType[]): Set<string> {
  const endpointKeyByWallId = new Map<string, [string, string]>();
  const wallIdsByEndpointKey = new Map<string, Set<string>>();
  const remainingWallIds = new Set<string>();

  walls.forEach((wall) => {
    const startKey = getEndpointKey(wall.start);
    const endKey = getEndpointKey(wall.end);
    endpointKeyByWallId.set(wall.id, [startKey, endKey]);
    remainingWallIds.add(wall.id);

    [startKey, endKey].forEach((key) => {
      const wallIds = wallIdsByEndpointKey.get(key) ?? new Set<string>();
      wallIds.add(wall.id);
      wallIdsByEndpointKey.set(key, wallIds);
    });
  });

  const endpointQueue = Array.from(wallIdsByEndpointKey.entries())
    .filter(([, wallIds]) => wallIds.size <= 1)
    .map(([endpointKey]) => endpointKey);

  while (endpointQueue.length > 0) {
    const endpointKey = endpointQueue.pop();
    if (!endpointKey) continue;

    const incidentWallIds = wallIdsByEndpointKey.get(endpointKey);
    if (!incidentWallIds || incidentWallIds.size > 1) continue;

    incidentWallIds.forEach((wallId) => {
      if (!remainingWallIds.has(wallId)) return;

      remainingWallIds.delete(wallId);
      endpointKeyByWallId.get(wallId)?.forEach((connectedEndpoint) => {
        const connectedWallIds = wallIdsByEndpointKey.get(connectedEndpoint);
        connectedWallIds?.delete(wallId);
        if (connectedWallIds && connectedWallIds.size <= 1) {
          endpointQueue.push(connectedEndpoint);
        }
      });
    });
  }

  return remainingWallIds;
}

function getEndpointKey(point: THREE.Vector2): string {
  const precision = 10000;
  return `${Math.round(point.x * precision)}:${Math.round(
    point.y * precision,
  )}`;
}

function getWallReferenceTangent(wall: WallType): THREE.Vector2 | null {
  if (wall.lineForm === "arc" && wall.controlPoint) {
    return getQuadraticBezierTangent(
      0.5,
      wall.start,
      wall.controlPoint,
      wall.end,
    )
      .clone()
      .normalize();
  }

  const tangent = wall.end.clone().sub(wall.start);
  return tangent.lengthSq() > 1e-8 ? tangent.normalize() : null;
}

export function wallCenter(wall: WallType): THREE.Vector2 {
  if (wall.lineForm === "arc" && wall.controlPoint) {
    return getQuadraticBezierPoint(
      0.5,
      wall.start,
      wall.controlPoint,
      wall.end,
    );
  }
  return wall.start.clone().add(wall.end).multiplyScalar(0.5);
}

export function isStructure3DSymbol(symbol: SymbolType): boolean {
  return (
    isSingleLeafDoorType(symbol) ||
    isDoubleDoorType(symbol) ||
    isWindowType(symbol) ||
    isVoidType(symbol)
  );
}

function removeDuplicatePoints(
  points: THREE.Vector2[],
  epsilon = 1e-7,
): THREE.Vector2[] {
  const cleanPoints: THREE.Vector2[] = [];
  points.forEach((point) => {
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
    const previous = cleanPoints[cleanPoints.length - 1];
    if (!previous || previous.distanceToSquared(point) > epsilon * epsilon) {
      cleanPoints.push(point.clone());
    }
  });

  if (
    cleanPoints.length > 1 &&
    cleanPoints[0].distanceToSquared(cleanPoints[cleanPoints.length - 1]) <=
      epsilon * epsilon
  ) {
    cleanPoints.pop();
  }

  return cleanPoints;
}

function convexHull(points: THREE.Vector2[]): THREE.Vector2[] {
  const unique = removeDuplicatePoints(
    [...points].sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x)),
  );
  if (unique.length <= 3) return unique;

  const cross = (
    origin: THREE.Vector2,
    a: THREE.Vector2,
    b: THREE.Vector2,
  ): number =>
    (a.x - origin.x) * (b.y - origin.y) - (a.y - origin.y) * (b.x - origin.x);

  const lower: THREE.Vector2[] = [];
  unique.forEach((point) => {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], point) <= 0
    ) {
      lower.pop();
    }
    lower.push(point);
  });

  const upper: THREE.Vector2[] = [];
  for (let i = unique.length - 1; i >= 0; i--) {
    const point = unique[i];
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], point) <= 0
    ) {
      upper.pop();
    }
    upper.push(point);
  }

  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

export function getWallColor(wall: WallType): THREE.Color {
  return new THREE.Color(resolveWallCapFillColor(wall));
}

export function getSymbolColor(symbol: SymbolType): THREE.Color {
  if (symbol.fillColor !== undefined) {
    return new THREE.Color(symbol.fillColor);
  }
  if (isSingleLeafDoorType(symbol) || isDoubleDoorType(symbol))
    return new THREE.Color(0x9a6b42);
  if (isWindowType(symbol)) return new THREE.Color(0x89b9d8);
  if (isVoidType(symbol)) return new THREE.Color(0x9ca3af);
  return new THREE.Color(0xa8a29e);
}

export function isWallLikeObject(
  object: WallType | SingleLineType,
): object is WallType {
  return isWallType(object);
}
